<template>
  <div class="note-taker-wrapper">
    <MessageScreen v-show="loading || errorLoading">
      <div class="loading-screen" v-if="errorLoading">
        <span>{{ errorLoadingMessage }}</span>
        <RoundedButton 
          text="Go back" 
          shadow 
          @click="goToSessionBrowser" 
          class="bottom" 
          :enterPress="goToSessionBrowser"
          ariaLabel="Go back"  
        />
      </div>
      <div class="loading-screen" v-else>
        <span>Loading...</span>
        <ProgressSpinner class="spinner bottom" />
      </div>
    </MessageScreen>

    <div v-show="!loading && !errorLoading" class="note-taker panels-wrapper">

      <div class="left-wrapper" style="outline: none" @keydown="handleKeypress" tabindex="0">
        <InfoPanel class="note-taking-info-panel" 
          :isOpen="infoPanelOpen" 
          :toggleInfoPanelOpen="toggleInfoPanelOpen"
          :goToSessionBrowser="goToSessionBrowser" 
          :pageTitle="mediaName" 
          :data="formatedInfoPanelSessionData"
          :editTitleData="{
            sessionId: this.$store.state.user.sessionDetails.id,
            sessionTitle: this.mediaName,
          }" 
          :bookmarkList="bookmarkList" 
          :pasteToNote="pasteToNote" 
          @updateTitle="updateTitle"
          @removeBookmark="removeBookmark" 
          @changeBookmarkColor="changeBookmarkColor" 
          @updateCurrentTime="
            (val) => {
              if (this.audioComponent) {
                this.audioComponent.currentTime = val;
                this.audioComponent.play();
              }
              if (this.videoComponent) {
                this.videoComponent.currentTime = val;
                this.videoComponent.play();
              }
            }
          " :isServiceProvider="isServiceProvider" 
          :sessionData="this.$store.state.user.sessionDetails">
        </InfoPanel>
      </div>


      <div class="content-panel" id="noteTakerContent">
        <section 
          title="Audio Player" 
          aria-label="Audio Player" 
          class="top-bar" 
          style="outline: none"
          @keydown="handleKeypress" 
          tabindex="0"
        >
          <div class="top-row-wrapper">
            <div class="left-section" :class="infoPanelOpen && !isDSA ? 'side-panel-open' : ''">
              <div class="session-name-wrapper" id="sessionNameWrapper" v-if="isDSA">
                <span class="session-name"> {{ sessionCurrentName }}</span>
                <IconButton 
                  :icon="ChevronLeftWhiteIcon" 
                  alt="Chevron Left" 
                  xsmall 
                  class="session-name-chevron-button"
                  v-tooltip.top="'Modify session name'" 
                  @click="toggleModifySessionNameMenu" 
                  :enterPress="toggleModifySessionNameMenu"
                  ariaLabel="Modify session name"
                  aria-haspopup="true" 
                  aria-label="Modify session name"  
                />
                <Popover 
                  ref="sessionNameMenu" 
                  class="change-session-name-menu"
                >
                  <div class="session-name-field">
                    <label for="sessionName">Session Name</label>
                    <InputText id="sessionName" type="text" aria-describedby="session-name-help"
                      v-model="sessionPreviewName" />
                  </div>
                  <div class="button-wrapper">
                    <RoundedButton 
                      text="Cancel" 
                      @click="cancelSessionName"
                      :enterPress="() => cancelSessionName"
                      ariaLabel="Cancel"
                      mediumwide 
                    />
                    <RoundedButton 
                      text="Save" 
                      @click="saveSessionName" 
                      :enterPress="() => saveSessionName"
                      ariaLabel="Save"
                      :disabled="!validSessionName" 
                      accent 
                      mediumwide 
                    />
                  </div>
                </Popover>
              </div>
            </div>

            <PDFViewerControlBar v-if="pdfSession" :numPages="pdfViewerNumPages" :currentPageNumber="pdfViewerCurrentPage"
              :switchToPdfPage="switchToPdfPage" :overviewPanelVisible="pdfOverviewPanelVisible"
              :setOverviewPanelVisible="setPdfOverviewPanelVisible" />
            <AudioControlBar
              v-else-if="!pdfSession && audioComponent && !isDSA && !(videoSession && !hideVideoPlayer)"
              :audioComponent="audioComponent"
              class="audio-control-bar"
              :transcriptAudioLength="this.audioComponent
                ? this.audioComponent.duration
                : this.videoComponent.duration
                "
              :audioVisualization="audioVisualization"
              :audioPaused="audioPaused"
              :audioVolume="audioVolume"
              :setPlaybackSpeed="setPlaybackSpeed"
              :playbackSpeed="playbackSpeed"
              :insertCurrentTimestamp="insertCurrentTimestamp"
              :mediaName="this.mediaName"
              :AUDIO_SKIP_AMOUNT="AUDIO_SKIP_AMOUNT_MEDIUM"
              :isInfoPanelOpen="infoPanelOpen"
              :addBookmark="() => insertBookmark('timestamp')"
              :disabled="isNewVoiceProcessing"
              @updateVolume="
                (val) => {
                  this.audioComponent.volume = val;
                  this.audioVolume = val;
                }
              "
              @updateCurrentTime="
                (val) => {
                  this.audioComponent.currentTime = val;
                }
              "
              @updateMuted="
                (val) => {
                  this.audioComponent.muted = val;
                }
              "
              @updateShowAudioVisualization="
                (val) => {
                  this.audioVisualization = val;
                }
              "
              @toggleAudioVisualization="addBookmarkListAudioVisualizer" 
                
            />
            <MediaControlBar
              v-if="!pdfSession && !isDSA && (videoReady || videoComponent)"
              :mediaComponent="audioComponent || videoComponent"
              class="audio-control-bar"
              :mediaType="this.audioComponent ? 'audio' : 'video'"
              :audioVisualization="audioVisualization"
              :mediaPaused="audioPaused"
              :insertCurrentTimestamp="insertCurrentTimestamp"
              :mediaName="mediaName"
              :AUDIO_SKIP_AMOUNT="AUDIO_SKIP_AMOUNT_MEDIUM"
              :isInfoPanelOpen="infoPanelOpen"
              :addBookmark="() => insertBookmark('timestamp')"
              :disabled="isNewVoiceProcessing"
              @updateCurrentTime="
                (val) => {
                  if (this.audioComponent) {
                    this.audioComponent.currentTime = val;
                  }
                }
              "
              @updateShowAudioVisualization="
                (val) => {
                  this.audioVisualization = val;
                }
              "
              @toggleAudioVisualization="addBookmarkListAudioVisualizer"
            />
            <div class="right-section">
              <div class="button-with-dropdown">
                <IconButton v-if="!pdfSession" :icon="PanelsWhiteIcon" alt="Panels" xsmall
                  v-tooltip.left="'Change layout'" />
                <div class="dropdown-menu right">
                  <button class="dropdown-menu-button" @click="
                    () => {
                      this.openTranscriptPanel();
                      this.openNotepadPanel();
                    }
                  ">
                    Transcript + Notes
                  </button>
                  <button class="dropdown-menu-button" @click="
                    () => {
                      this.openTranscriptPanel();
                      this.closeNotepadPanel();
                    }
                  ">
                    Transcript Only
                  </button>
                  <button class="dropdown-menu-button" @click="
                    () => {
                      this.closeTranscriptPanel();
                      this.openNotepadPanel();
                    }
                  ">
                    Notes Only
                  </button>
                </div>
              </div>
              <Dropdown
                :key="openMainMenuDropdown"
                size="large"
                buttonIcon="pi-ellipsis-v"
                :menuItems="mainMenuDropdown"
                :dropdownOpenByParent="openMainMenuDropdown"
              >
                <template #ttsvoices>
                  <div class="tts-voices">
                    <Dropdown
                      :buttonLabel="selectedAccent.split(' ')[0]"
                      :selectedMenuItem="selectedAccent"
                      :menuItems="accents"
                      group="ttsvoices"
                      shade="lighter"
                    />
                    <Dropdown
                      :buttonLabel="selectedTTSVoice"
                      :selectedMenuItem="selectedTTSVoice"
                      :menuItems="ttsVoices"
                      group="ttsvoices"
                      shade="lighter"
                    />
                  </div>
                </template>
              </Dropdown>
            </div>
          </div>
          <div 
            class="audio-visualization-bar" 
            :class="audioVisualization ? 'show' : ''" 
            v-if="!videoControl"
            style="outline: none" 
            @keydown="handleKeypress" 
            tabindex="0"
          >
            <AudioVisualizer v-if="audioComponent || videoComponent" 
              :audioComponent="audioComponent || videoComponent"
              ref="audioVisualizer" 
              @playbackAt="playbackAt" 
            />
          </div>
        </section>

        <div class="tts-warning" v-if="isNewVoiceProcessing && transcriptAvailable">
          <ProgressSpinner class="tts-warning__spinner"/>
          Your voice is being generated...
        </div>

        <div class="main">
          <PDFOverviewPanel 
            class="pdf-overview-panel" 
            v-if="pdfSession" 
            :visible="pdfOverviewPanelVisible"
            :currentPageNumber="pdfViewerCurrentPage" 
            :numPages="pdfViewerNumPages" 
            :switchToPdfPage="switchToPdfPage"
            :pdfData="pdfData" 
            @hide="this.pdfOverviewPanelVisible = false" 
          />
          <div class="editors">
            <div v-if="!transcriptAvailable && isNewVoiceProcessing" class="voice-loading-screen">
              <ProgressSpinner class="spinner bottom" />
            </div>
            <div v-if="pdfSession" 
              class="pdf-viewer-panel panel note-taker-panel note-taker-editor-wrapper"
              ref="pdfPanel" 
              :class="[{ collapsed: !isTranscriptOpen }]">
              <section title="PDF Panel" aria-label="PDF Panel">
                <div class="editor-type-header" style="outline: none" @keydown="handleKeypress" tabindex="0">
                  <span class="editor-type-header-text">
                    <img :src="TranscriptWhiteIcon" />
                    Document
                  </span>
                </div>
                <div class="pdf-viewer">
                  <vue-pdf-embed 
                    ref="pdfViewer" 
                    :source="pdfData" 
                    :page="pdfViewerCurrentPage" 
                    :textLayer="true"
                    @rendered="handlePdfRender" 
                    :style="'scale:' + pdfViewerScale" 
                  />
                </div>
              </section>
            </div>

            <div v-else class="note-taker-editor-wrapper note-taker-panel panel"
              :class="[{ collapsed: !isTranscriptOpen }]">
              <section title="Transcript Panel" aria-label="Transcript Panel">
                <portal-target name="above-transcript"> </portal-target>
                <div class="editor-type-header" style="outline: none" @keydown="handleKeypress" tabindex="0">
                  <div class="download-button-wrapper">
                    <span tabindex="-1" aria-hidden="true" class="editor-type-header-text">
                      <img :src="TranscriptWhiteIcon" />
                      Transcript
                    </span>
                    <IconButton 
                      :icon="DownloadGreyIcon" 
                      alt="Download" 
                      :enterPress="() => saveToComputer(PANEL_TYPES.TRANSCRIPT)" 
                      @click="saveToComputer(PANEL_TYPES.TRANSCRIPT)"
                      xxxsmall 
                      class="download-button2" 
                      ariaLabel="Download Transcript" 
                    />
                  </div>
                </div>

                <AbstractSummary 
                  :class="[{ collapsed: !isTranscriptOpen }]" :abstractText="summariseAbstract"
                  v-if="this.$store.getters['user/currentUserSubscriptionActive']()" 
                />
                <TranscriptEditor
                  v-if="savedTranscript"
                  class="text-editor"
                  :savedTranscript="savedTranscript"
                  :class="[{ collapsed: !isTranscriptOpen }]"
                  :id="this.$store.state.user.sessionDetails.id + '-transcript'"
                  :handleKeypress="handleKeypress"
                  :transcriptEditor="transcriptEditor"
                  :setTranscriptEditor="setTranscriptEditor"
                  :fontColour="fontColourTranscript"
                  :fontSize="fontSizeTranscript"
                  :fontBold="fontBoldTranscript"
                  :fontFamily="fontFamilyTranscript"
                  :lineHeight="lineHeightTranscript"
                  :letterSpacing="letterSpacingTranscript"
                  :tiptapToken="tiptapToken"
                  ref="transcriptEditorRef" 
                  @openStudyAssistantModal="openStudyAssistantModal"
                />
              </section>
            </div>

            <div class="note-taker-editor-wrapper note-editor-wrapper note-taker-panel panel"
              :class="[{ collapsed: !isNotepadOpen }]">
              <section title="Notes Panel" aria-label="Notes Panel">
                <portal-target name="above-personal-note"> </portal-target>
                <div v-if="!isWorker" class="editor-type-header tabs">
                  <div class="note-editor-selection-buttons" v-if="!pdfSession && (hasProNoteAccess || canShowProNote)">
                    <button 
                      class="editor-type-header-text clickable" 
                      :class="{ 'active-editor-button': showProNotes }"
                      @click="showProNotes = true"
                      aria-label="Requested notes"
                      @keydown="($event) => {
                        if ($event.key === 'Enter') {
                          $event.preventDefault();
                          showProNotes = true;
                        }
                      }"
                    >
                        <img :src="NotesWhiteIcon" />
                        Requested Notes
                    </button>

                    <button 
                      class="editor-type-header-text clickable" 
                      :class="{
                      'active-editor-button': !showProNotes,
                      }" 
                      @click="showProNotes = false"
                      aria-label="Personal notes"
                      @keydown="($event) => {
                        if ($event.key === 'Enter') {
                          $event.preventDefault();
                          showProNotes = false;
                        }
                      }"  
                    >
                      <img :src="PencilWhiteIcon" />
                      Personal Notes
                    </button>
                  </div>
                  <div v-else class="editor-type-header" style="outline: none" @keydown="handleKeypress" tabindex="0">
                    <span class="editor-type-header-text">
                      <img :src="PencilWhiteIcon" />
                      Personal Notes
                    </span>
                  </div>
                </div>
                <section role="region" title="Text Formatting Bar" aria-label="Text Formatting Bar" class="text-formatting-bar-wrapper" v-if="!showProNotes || canShowProNote" :class="{
                  'disable-and-blur': disablePersonalNote && !showProNotes,
                }">
                  <TextFormattingBar :editor="noteEditor" :zoomFactor="zoomFactorNotes"
                    :setZoomFactor="setNotesZoomFactor" :addImage="addNotesImage" :showEmoji="showNotesEmoji"
                    :highlightedFontSize="highlightedFontSize" :setHighlightedFontSize="setHighlightedFontSize"
                    :disabledAll="showProNotes" />
                  <IconButton 
                    :icon="DownloadGreyIcon" 
                    alt="Download" 
                    @click="saveToComputer(PANEL_TYPES.NOTES)"
                    xxxsmall 
                    class="download-button" 
                    aria-label="Download"  
                    :enterPress="() => saveToComputer(PANEL_TYPES.NOTES)"
                  />
              </section>
                <NoteEditor v-if="
                  (((((audioComponent || videoComponent) && tiptapToken) || pdfSession) &&
                    !showProNotes) || isServiceProvider)
                " ref="personalNote" class="text-editor note-editor" :class="[{ collapsed: !isNotepadOpen }]"
                  :id="this.$store.state.user.sessionDetails.id" :noteEditor="noteEditor" :setNoteEditor="setNoteEditor"
                  :audioComponent="audioComponent" :insertCurrentTimestamp="insertCurrentTimestamp" :editable="!disablePersonalNote"
                  :zoomFactor="zoomFactorNotes" :isPersonalNote="true" :disablePersonalNote="disablePersonalNote"
                  :condensedDisabledDialog="true" :showDisablePersonalNoteDialog="disablePersonalNote &&
                    this.videoElementDestination !== 'above-personal-note'
                    " :setSubscribeVisible="setShowSubscriptionSignupDialog" 
                    :tiptapToken="tiptapToken"
                    @openStudyAssistantModal="openStudyAssistantModal"
                />
                <ProNoteRequestStatus v-if="
                  (audioComponent || pdfSession || videoComponent) &&
                  showProNotes &&
                  !canShowProNote
                " :openRequestNoteForm="() => (orderFormVisible = true)" />
                <NoteEditor v-if="
                  (audioComponent || pdfSession || videoComponent) &&
                  showProNotes &&
                  canShowProNote
                " class="text-editor note-editor" :class="[{ collapsed: !isNotepadOpen }]"
                  :id="this.$store.state.user.sessionDetails.id + '-ProNotes'" :noteEditor="proNoteEditor"
                  :setNoteEditor="setNoteEditor" :audioComponent="audioComponent"
                  :insertCurrentTimestamp="insertCurrentTimestamp" :editable="false" :zoomFactor="zoomFactorNotes"
                  :isProNote="true"
                  :showProNoteAlert="showProNoteAlert" @closeProNoteAlert="closeProNoteAlert()"
                  :setLegacyNoteEditor="setProNoteEditor"
                />
                <div class="editor-bottom-bar" v-if="isServiceProvider">
                  <RoundedSquareButton :image="FeedbackBubblePurpleIcon" imageAlt="Insert feedback link" :onClick="() => {
                    this.noteEditor.commands.insertContent(
                      this.feedbackAnchorTag
                    );
                  }
                    " tall v-tooltip.top="'Insert provide feedback link'" />
                  <RoundedButton text="Submit Notes" class="submit-notes-button" bold shadow lessRound
                    @click="submitNotes" aria-label="Submit Notes" :enterPress="() => submitNotes()" />
                </div>
              </section>
            </div>
            <div class="study-assistant-wrapper note-taker-panel panel note-taker-editor-wrapper"
              :class="[{ collapsed: !isStudyAssistantOpen }]" v-if="!isServiceProvider && !pdfSession">
              <section title="Study Assistant Panel" aria-label="Study Assistant Panel">
                <div class="editor-type-header" style="outline: none" @keydown="handleKeypress" tabindex="0">
                  <span class="editor-type-header-text"> Study Assistant </span>
                </div>
                <StudyAssistant
                  :questionsAnswersList="studyAssistantQuestionsAnswersList" :pushToQuestionsAnswersList="pushToStudyAssistantQuestionsAnswersList"
                  :quizList="summariseQuizList"
                  :summarizeList="[]"
                  :keywordsList="summariseKeywordsList"
                  :recommendationsList="summariseRecommendationsList"
                  :pasteToNote="pasteToNote"
                  tipsString=""
                />
              </section>
            </div>
          </div>
        </div>
        <div class="panels-footer" v-if="!isServiceProvider && !pdfSession && transcriptAvailable">
          <IconButton :icon="MagicWandBlackIcon" alt="Toggle Study Assistant"
            v-tooltip.top="'Toggle Study Assistant panel'" class="toggle-study-assistant-button"
            :onClick="toggleStudyAssistantPanel" ariaLabel="Toggle Study Assistant" :enter-press="() => toggleStudyAssistantPanel()"/>
        </div>
      </div>
    </div>

    <StudyAssistantDropdown
      v-if="transcriptEditor && !playing"
      :actions="actions"
      editorName="transcript"
      :editor="transcriptEditor"
      :hideDropdown="showStudyAssistantModal"
      :tiptapToken="tiptapToken"
      :proUser="this.$store.getters['user/currentUserSubscriptionActive']()"
      @updateAudioVideoTime="updateAudioVideoTime"
    />
    <StudyAssistantDropdown
      v-if="noteEditor && !playing"
      :actions="actions"
      editorName="note"
      :editor="noteEditor"
      :hideDropdown="showStudyAssistantModal"
      :tiptapToken="tiptapToken"
      :proUser="this.$store.getters['user/currentUserSubscriptionActive']()"
    />
    <StudyAssistantModal 
      :showModal="showStudyAssistantModal"
      :modalInfo="studyAssistantModal"
      :modalIndex="studyAssistantModalIndex"
      :MagicWandPinkIcon="MagicWandPinkIcon"
      :footerActions="footerActions"
      @regenerateAIOutput="regenerateAIOutput"
      @changeStudyAssistantModalIndex="changeStudyAssistantModalIndex"
      @closeStudyAssistantModal="closeStudyAssistantModal"
    />

    <Dialog class="submit-notes-dialog" 
      :header="!notesSubmitted ? 'Submit or Update task' : 'Notes submitted!'"
      :visible="displaySubmitNotesModal" 
      @update:visible="closeSubmitNotesModal" 
      :style="{ width: '50vw' }"
      :modal="true"
      closeOnEscape
      >
      <div v-if="!notesSubmitted && !errorSubmittingNotes">
        <div class="task-type">Task type: Note-Taking</div>
        <label for="notes">NOTES</label>
        <textarea id="notes" rows="5"></textarea>
      </div>
      <div v-else-if="errorSubmittingNotes">
        <div class="notes-submitted-text">
          There was an error while submitting your notes.
        </div>
      </div>
      <div v-else>
        <div class="notes-submitted-text">
          Your notes have been sent to the student who requested them, and any
          other recipients listed in the booking. We have also updated the
          status of the task to completed.
        </div>
      </div>
      <template #footer>
        <RoundedButton 
          v-if="!notesSubmitted" 
          text="Cancel" 
          @click="closeSubmitNotesModal" 
          lessRound 
          grey 
          largerFont
          wide 
          morePadding 
          fitHeight 
        />
        <RoundedButton 
          v-if="!notesSubmitted && !errorSubmittingNotes" 
          text="Submit Notes" 
          @click="submitNotesConfirmed"
          :loading="uploadingFileToBeehive" 
          accent 
          lessRound 
          wide 
          largerFont 
          morePadding 
          fitHeight 
        />
        <RoundedButton 
          v-if="errorSubmittingNotes" 
          text="Try again" 
          @click="
            () => {
              this.errorSubmittingNotes = false;
            }
          " 
          :loading="uploadingFileToBeehive" 
          accent 
          lessRound 
          wide 
          largerFont 
          morePadding 
          fitHeight 
        />
        <RoundedButton 
          v-if="notesSubmitted" 
          text="OK" 
          @click="closeSubmitNotesModal" 
          accent 
          lessRound 
          wide 
          largerFont
          morePadding 
          fitHeight 
        />
      </template>
    </Dialog>


    <Dialog class="coming-soon-dialog" 
      :header="false" 
      :closable="false" 
      closeOnEscape
      v-model:visible="displayComingSoonDialog" 
      :modal="true"
    >
      <div class="coming-soon-dialog-iconwrapper">
        <IconButton :icon="CloseGreyIcon" class="close-button" alt="Close" :onclick="() => {
          this.displayComingSoonDialog = false;
        }
          " xsmall />
      </div>
      <div class="coming-soon-dialog-header">Coming soon!</div>
      <div class="coming-soon-dialog-body">
        This feature is still in the works.<br />
        Thank you for your patience.
      </div>
      <div class="coming-soon-dialog-buttonwrapper">
        <Button 
          :onclick="() => (this.displayComingSoonDialog = false)" 
          v-on:keydown="($event) => 
          {
            if ($event.key === 'Enter') {
              $event.preventDefault();
              this.displayComingSoonDialog = false;
            }
          }
          "
          :size="large" 
          label="OK" 
          text 
          rounded
          class="coming-soon-dialog-buttonwrapper-button"
        />
      </div>
    </Dialog>
    <Dialog
      class="processing-dialog"
      :header="false"
      :closable="false"
      closeOnEscape
      v-model:visible="displayProcessingDialog"
      :modal="true"
    >
      <div class="processing-dialog__header">
        Your new voice is being generated
      </div>
      <div class="processing-dialog__body">
        This may take a few minutes. You can wait until the process is complete, or switch to a previously generated voice.
      </div>
      <div class="processing-dialog__footer">
        <RoundedButton
          class="pink"
          text="Continue waiting"
          lessRound
          @click="displayProcessingDialog = false"
          :enterPress="() => displayProcessingDialog = false"
        />
        <RoundedButton
          class="white"
          text="Select another voice"
          lessRound
          @click="selectAnotherVoice"
          :enterPress="selectAnotherVoice"
        />
      </div>
    </Dialog>


    <SubscriptionSignup :visible="showSubscriptionSignupDialog" :setVisible="setShowSubscriptionSignupDialog" />

    <Toast position="bottom-center" :style="{ marginBottom: '10rem' }" group="bc" :closeButton="null">
      <template #message="slotProps">
        <img :src="BookmarkFilledIcon" alt="BookmarkFilled" />
        <div>
          {{ slotProps.message.summary }}
        </div>
      </template>
    </Toast>
    <Toast
      position="bottom-center"
      :style="{ marginBottom: '10rem' }"
      group="tts"
      :closeButton="null"
    >
      <template #message="slotProps">
        <img :src="AudioVisualizerIcon" alt="AudioVisualizerIcon" />
        {{ slotProps.message.summary }}
      </template>
    </Toast>
    <Toast
      position="top-center"
      group="notification"
      class="itemlist-notification-toast-wrapper"
    >
    </Toast>

    
    <RequestNotesOrderForm :visible="orderFormVisible" v-if="!isWorker && transcriptAvailable"
      :recordingTitle="$store.state.user.sessionDetails.title" :durationHMS="convertMillisecondsToHMS($store.state.user.sessionDetails.mediaUploadData.duration)
        " :durationMilliseconds="$store.state.user.sessionDetails.mediaUploadData.duration?.toString()
          " :recordingUploadId="$store.state.user.sessionDetails.mediaUploadData.id"
      :setOrderFormVisible="setOrderFormVisible" @updateStandbySessionsMenuItem="updateStandbySessionsMenuItem()"
      ref="orderForm" @updateSessionList="updateProNoteRequest"></RequestNotesOrderForm>
    <portal :to="videoElementDestination">
      <Transition name="slide">
        <div
          id="video-wrapper"
          :class="{
            'note-taker-video-collapse': toggleVideoTile,
          }"
          :style="videoHeightFix && { height: '55vh' }"
          class="note-taker-video-wrapper"
          v-if="videoSession && !hideVideoPlayer && transcriptAvailable"
        >
          <video-player
            ref="videoPlayer"
            id="video"
            class="note-taker-video"
            :src="videoUrl"
            :controls="videoControl"
            :loop="false"
            :volume="0.6"
            @enterpictureinpicture="setToggleVideoTile(true)"
            @leavepictureinpicture="setToggleVideoTile(false)"
            @play="startPlaying"
            @pause="pausePlaying"
            @timeupdate="playing && highlightCurrentText()"
            @mounted="onMountedVideoPlayer"
            @unmounted="onUnmountedVideoPlayer"
          />
          <IconButton v-if="videoComponent && videoComponent.requestPictureInPicture" 
            class="pip-mode-button"
            :class="{ 'note-taker-video-collapse': toggleVideoTile }" 
            alt="Minimize - Enter Picture-In-Picture Mode"
            @click="setPIPMode" 
            v-tooltip.top="'Minimize video to picture-in-picture mode'"
            :aria-label="videoComponent?.pipMode ? 'Exit Picture-in-Picture mode' : 'Enter Picture-in-Picture mode'"
            :enter-press="setPIPMode"
            :aria-haspopup="true"
            :aria-expanded="videoComponent?.pipMode"
            :aria-controls="videoComponent?.pipMode ? 'video-wrapper' : ''"
            :icon="MinimizeIcon" 
            small 
          />
          <IconButton 
            class="pip-mode-button position-toggle-button"
            :class="{ 'note-taker-video-collapse': toggleVideoTile }" 
            @click="toggleVideoPosition"
            v-tooltip.top="'Change video position'"
            :aria-label="videoElementDestination === 'above-transcript' ? 'Change video tile position to top right' : 'Change video tile position to top left'"
            :enter-press="toggleVideoPosition"
            alt="Change Video Tile Position" 
            :icon="this.videoElementDestination === 'above-transcript'
              ? PositionTopRightIcon
              : PositionTopLeftIcon
              " small 
          />
        </div>
      </Transition>
    </portal>
  </div>
</template>

<script>
import InfoPanel from "../components/InfoPanel";
import TranscriptEditor from "../components/NoteTaking/TranscriptEditor.vue";
import NoteEditor from "../components/NoteTaking/NoteEditor.vue";
import IconButton from "../components/IconButton.vue";
import Session from "../components/Helpers/Session";
import AudioControlBar from "../components/NoteTaking/AudioControlBar";
import MediaControlBar from "../components/NoteTaking/MediaControlBar";
import RoundedSquareButton from "../components/RoundedSquareButton";
import BackArrowIcon from "../assets/images/BackArrow.svg";
import BookmarkIcon from "../assets/images/Bookmark.svg";
import ChevronLeftWhiteIcon from "../assets/images/Chevron-left-white.svg";
import PanelsWhiteIcon from "../assets/images/Panels-white.svg";
import ExportUploadWhiteIcon from "../assets/images/Export-upload-white.svg";
import LightbulbPurpleIcon from "../assets/images/Lightbulb-purple.svg";
import AudioVisualizationExample from "../assets/images/AudioVisualizationExample.svg";
import ArrowRightBlackIcon from "../assets/images/Arrow-right-black.svg";
import DownloadGreyIcon from "../assets/images/Download-grey.svg";
import MagicWandLight from "../assets/images/Magic-wand-light.svg";
import FeedbackBubblePurpleIcon from "../assets/images/Feedback-bubble-purple.svg";
import TranscriptWhiteIcon from "../assets/images/Transcript-white.svg";
import NotesWhiteIcon from "../assets/images/Notes-white.svg";
import PencilWhiteIcon from "../assets/images/Pencil-white.svg";
import MessageScreen from "../components/MessageScreen.vue";
import RoundedButton from "../components/RoundedButton";
import TextFormattingBar from "../components/NoteTaking/TextFormattingBar";
import StudyAssistant from "../components/NoteTaking/StudyAssistant";
import RequestNotesOrderForm from "../components/Orders/RequestNotesOrderForm";
import {
  ACCOUNT_TYPES,
  ACCOUNT_TEAMS,
  dbConstants,
  editorConstants,
  STUDY_ASSISTANT_PROMPT_TYPES,
  NOTES_PANEL_DEFAULTS,
  HIGHLIGHT_COLOR_MAP,
  ORDER_STATUS,
  ACCENTS
} from "../assets/constants";
import Averta from "../assets/fonts/Averta-Regular.otf";
import {
  convertDateToHoursMinutes,
  convertDateToMonthDayYear,
  convertMillisecondsToHMS,
  convertSecondsToMS,
  formatDate,
  getTimeCodeFromNum,
} from "../assets/helpers_legacy";
import { makeMPServerRequest } from "../assets/helpers";
import AWS from "aws-sdk";
import axios from "axios";
import { TextSelection } from "prosemirror-state";
import { ref } from "vue";
import CloseGreyIcon from "../assets/images/Close-grey.svg";
import PDFViewerControlBar from "../components/NoteTaking/PDFViewerControlBar";
import PDFOverviewPanel from "../components/NoteTaking/PDFOverviewPanel";
import VuePdfEmbed from "vue-pdf-embed";
import _ from "lodash";
import { downloadFile } from "../assets/downloadFile.js";
import { uploadDocument } from '../assets/uploadFile.js';
import SubscriptionSignup from "../components/Onboarding/SubscriptionSignup.vue";
import ProNoteRequestStatus from "../components/NoteTaking/ProNoteRequestStatus.vue";
import AudioVisualizer from "../components/NoteTaking/AudioVisualizer.vue";
import DeleteIcon from "../assets/images/Delete-bookmark.svg";
import CopyIcon from "../assets/images/Copy-white.svg";
import AddToNotesIcon from "../assets/images/AddToNotes-white.svg";
import BookmarkFilledIcon from "../assets/images/BookmarkFilled.svg";
import PositionTopRightIcon from "../assets/images/PositionTopRight.svg";
import PositionTopLeftIcon from "../assets/images/PositionTopLeft.svg";
import MinimizeIcon from "../assets/images/Reduce.svg";
import SimplifyTextIcon from "../assets/images/SimplifyText.svg";
import RewriteTextIcon from "../assets/images/RewriteText.svg";
import MagicWandPinkIcon from "../assets/images/Magic-wand-pink.svg";
import PurpleCopyIcon from "../assets/images/Copy-purple.svg";
import PurpleAddToNotesIcon from "../assets/images/AddToNotes.svg";
import AutoScrollIcon from "../assets/images/Autoscroll-white.svg";
import RequestProNoteIcon from "../assets/images/RequestNotesIcon.svg";
import UploadWhiteIcon from "../assets/images/Upload-white.svg";
import AudioVisualizerIcon from "../assets/images/Audio-visualization-white.svg";
import TTSIcon from "../assets/images/TTS.svg";
import ReadAloudIcon from "../assets/images/ReadAloud.svg";
import DefineTextIcon from "../assets/images/DefineTextIcon.svg";
import ProofreadTextIcon from "../assets/images/ProofreadText.svg";
import { VideoPlayer } from "@videojs-player/vue";
import AbstractSummary from "../components/NoteTaking/AbstractSummary";
import StudyAssistantDropdown from "../components/NoteTaking/StudyAssistantDropdown.vue";
import StudyAssistantModal from "../components/NoteTaking/StudyAssistantModal.vue";
import Dropdown from "../components/Dropdown.vue";
import "video.js/dist/video-js.css";

export default {
  extends: Session,

  components: {
    InfoPanel,
    TranscriptEditor,
    NoteEditor,
    AudioControlBar,
    MediaControlBar,
    IconButton,
    MessageScreen,
    RoundedButton,
    TextFormattingBar,
    StudyAssistant,
    RoundedSquareButton,
    PDFViewerControlBar,
    PDFOverviewPanel,
    VuePdfEmbed,
    SubscriptionSignup,
    RequestNotesOrderForm,
    ProNoteRequestStatus,
    AudioVisualizer,
    VideoPlayer,
    AbstractSummary,
    StudyAssistantDropdown,
    StudyAssistantModal,
    Dropdown,
  },

  data() {
    return {
      pdfSession: this.$store.state.user.sessionDetails.sessionType === "document",

      PANEL_TYPES: {
        TRANSCRIPT: "transcript",
        NOTES: "notes",
      },
      AUDIO_SKIP_AMOUNT_SMALL: 3,
      AUDIO_SKIP_AMOUNT_MEDIUM: 10,
      AUDIO_SKIP_AMOUNT_LARGE: 60,
      VOLUME_ADJUST_AMOUNT: 0.1,
      PLAYBACK_SPEED_ADJUST_AMOUNT: 0.25,

      editorConstants: editorConstants,
      BackArrowIcon,
      BookmarkIcon,
      ChevronLeftWhiteIcon,
      PanelsWhiteIcon,
      ExportUploadWhiteIcon,
      LightbulbPurpleIcon,
      ArrowRightBlackIcon,
      AudioVisualizationExample,
      DownloadGreyIcon,
      MagicWandLight,
      FeedbackBubblePurpleIcon,
      CloseGreyIcon,
      TranscriptWhiteIcon,
      NotesWhiteIcon,
      PencilWhiteIcon,
      DeleteIcon,
      CopyIcon,
      AddToNotesIcon,
      BookmarkFilledIcon,
      PositionTopRightIcon,
      PositionTopLeftIcon,
      MinimizeIcon,
      SimplifyTextIcon,
      RewriteTextIcon,
      MagicWandPinkIcon,
      AutoScrollIcon,
      RequestProNoteIcon,
      UploadWhiteIcon,
      AudioVisualizerIcon,
      TTSIcon,
      ReadAloudIcon,
      DefineTextIcon,
      ProofreadTextIcon,

      convertMillisecondsToHMS,
      convertDateToMonthDayYear,
      convertDateToHoursMinutes,
      Averta,
      isNotepadOpen: true,
      isTranscriptOpen: true,
      isSidePanelOpen: true,
      isStudyAssistantOpen: true,
      loading: true,
      errorLoading: false,
      errorLoadingMessage: "",
      MIN_LOADING_TIME: 1000,
      minLoadingTimeElapsed: false,
      audioVisualization: false,
      audioComponent: null,
      infoPanelOpen: false,
      shouldCloseInfoPanel: false,
      activeSidePanel: null,
      audioPaused: true,
      audioVolume: 1,
      playbackSpeed: 1,
      savedTranscript: null,
      noteEditor: null,
      transcriptEditor: null,
      sessionCurrentName: "Session Name",
      sessionPreviewName: "Session Name",
      transcriptAudioPlayer: ref(null),
      transcriptAudioCanvas: ref(null),
      mediaControlsFloating: true,
      backupNotesInterval: null,
      uploadingFileToBeehive: false,
      panelToSave: "",
      zoomFactorNotes: 1,
      fontColourTranscript: editorConstants.WHITE,
      fontBoldTranscript: editorConstants.DEFAULT_FONT_BOLD,
      fontFamilyTranscript: editorConstants.AVERTA,
      fontSizeTranscript: editorConstants.DEFAULT_FONT_SIZE,
      lineHeightTranscript: 1.4,
      letterSpacingTranscript: 0,
      highlightedFontSize: NOTES_PANEL_DEFAULTS.FONT_SIZE,
      showProNotes: false,
      proNoteEditor: null,
      summariseAbstract: "",

      // PDF Viewer variables
      pdfData: this.$store.state.user.sessionDetails.documentUrl || undefined,
      pdfViewerCurrentPage: null,
      pdfViewerNumPages: 0,
      pdfOverviewPanelVisible: false,
      pdfViewerInitialWidth: null,
      pdfViewerScale: 1,
      isInitialPdfRender: true,

      // Video Session variables
      videoUrl: this.$store.state.user.sessionDetails.videoUrl,
      videoSession: this.$store.state.user.sessionDetails.sessionType === "video",
      toggleVideoTile: false,
      videoComponent: null,
      videoDuration: -1,
      // either "above-transcript" or "above-personal-note"
      videoElementDestination: "above-transcript",
      videoState: null,
      videoReady: false,

      transcriptTimestampFrequency: 4,

      studyAssistantQuestionsAnswersList: [
        {
          text:
            "Hi! 👋 I'm your study assistant. " +
            "Let me know what you're looking for.",
          type: "",
          timestamp: new Date(),
        },
        {
          prompt: STUDY_ASSISTANT_PROMPT_TYPES.QUIZ_AND_TEST_PREP,
          type: "selectable",
          timestamp: new Date(),
        },
        {
          prompt: STUDY_ASSISTANT_PROMPT_TYPES.STUDY_MATERIAL_ANALYSIS,
          type: "selectable",
          timestamp: new Date(),
        },
      ],

      displayComingSoonDialog: false,
      summariseQuizList: undefined,
      summariseKeywordsList: undefined,
      summariseRecommendationsList: undefined,

      mediaName: "",

      showSubscriptionSignupDialog: false,
      orderFormVisible: false,
      showProNoteAlert: this.$store.state.user.sessionDetails.status === "done",
      bookmarkList: [],
      bookmarkTracker: {},
      formatedInfoPanelSessionData: [],

      audioDuration: -1,

      showStudyAssistantModal: false,
      studyAssistantModal: {
        isLoading: false,
        editor: null,
        errorMessage: "",
        warningMessage: "",
        header: "",
        body: []
      },
      studyAssistantModalIndex: 0,
      footerActions: [
      {
          name: 'Copy',
          image: PurpleCopyIcon,
          onclick: this.copyText
        },
        {
          name: 'Add to notes',
          image: PurpleAddToNotesIcon,
          onclick: this.addToNotes
        }
      ],
      bookmarkPos: null,
      tiptapToken: null,

      hideVideoPlayer: false,
      playing: false,
      timestampEnabled: localStorage.getItem("timestampEnabled") === "false" ? false : true,
      autoScrollEnabled: localStorage.getItem("autoScrollEnabled") === "false" ? false : true,
      selectedTTSVoice: 'Default',
      voiceList: [],
      scrollableParagraphId: null,
      transcriptChangeInProgress: false,
      accents: ACCENTS.map(accent => ({
        ...accent,
        onClick: () => {
          this.selectedAccent = accent.name;
        }
      })),
      selectedAccent: 'All Voices',
      displayProcessingDialog: false,
      openMainMenuDropdown: false,
      speakerEnabled: false,
    };
  },

  watch: {
    async transcriptTimestampFrequency() {
      await this.setTranscriptText();
      this.$refs.transcriptEditorRef.transcriptEditor.commands.setContent(
        this.savedTranscript
      );
    },

    audioDuration: {
      handler(newVal, oldVal) {
        if (oldVal < 0) {
          const durationString = convertMillisecondsToHMS(newVal).split(":");
          const durationFormated =
            `${durationString[0] !== "00" ? durationString[0] + " hr " : ""}` +
            `${durationString[1] !== "00" ? durationString[1] + " min " : ""}` +
            `${durationString[2]} sec`;
          this.formatedInfoPanelSessionData.push({
            title: "Duration",
            info: [
              {
                text: durationFormated,
                type: "primary",
              },
            ],
          });
        }
      },
      once: true,
    },

    audioComponent: {
      handler(newVal, oldVal) {
        if (newVal && !oldVal) {
          newVal.addEventListener("play", () => {
            this.audioPaused = false;
          });
          newVal.addEventListener("pause", () => {
            this.audioPaused = true;
          });
        }
      },
      once: true,
    },
  },

  computed: {
    isServiceProvider() {
      switch (this.$store.state.user.currentUser.type.toLowerCase()) {
        case "worker":
        case "service_provider":
          return true;
        default:
          return false;
      }
    },

    validSessionName() {
      return this.sessionPreviewName.trim().length > 0;
    },

    isPaused() {
      return this.audioComponent.paused;
    },

    highlightedTranscriptText() {
      if (this.transcriptEditor) {
        const state = this.transcriptEditor.state;
        const word = state.doc.textBetween(
          Math.min(state.selection.$head.pos, state.selection.$anchor.pos),
          Math.max(state.selection.$head.pos, state.selection.$anchor.pos),
          " "
        );
        return word;
      } else {
        return "";
      }
    },

    isDSA() {
      return this.$store.state.user.currentUser.team === ACCOUNT_TEAMS.DSA;
    },

    isWorker() {
      return this.$store.state.user.currentUser.type === ACCOUNT_TYPES.WORKER;
    },

    isRecordingSessionType() {
      return this.$store.state.user.sessionDetails.sessionType === "recording";
    },

    isDocumentSessionType() {
      return this.$store.state.user.sessionDetails.sessionType === "document";
    },

    isVideoSessionType() {
      return this.$store.state.user.sessionDetails.sessionType === "video";
    },

    canShowProNote() {
      return (
        this.$store.state.user.sessionDetails.status === "completed" ||
        this.$store.state.user.sessionDetails.status === "done"
      );
    },

    proNoteRequested() {
      return this.$store.state.user.sessionDetails.status !== "no-value";
    },

    disablePersonalNoteAndTimestamp() {
      return (
        !this.$store.getters['user/currentUserSubscriptionActive']()
      );
    },

    disablePersonalNote() {
      return (
        !this.$store.getters['user/currentUserSubscriptionActive']()
      );
    },

    mainMenuDropdown() {
      const menuItems = [
        {
          name: 'Download notes',
          icon: NotesWhiteIcon,
          endOfSection: true,
          onClick: () => this.saveToComputer(this.PANEL_TYPES.NOTES)
        }
      ];

      // Show menu items only if it is an audio or video session
      if (this.videoComponent || this.audioComponent) {
        menuItems.unshift({
          name: 'Download transcript',
          icon: TranscriptWhiteIcon,
          onClick: () => this.saveToComputer(this.PANEL_TYPES.TRANSCRIPT)
        });

        menuItems.push({
          name: 'Timestamp',
          description: 'Display or hide timestamps in the transcript for each corresponding segment of text.',
          checked: this.timestampEnabled,
          disabled: this.transcriptChangeInProgress,
          onToggle: this.toggleTimestamp
        });
      }

      if ((this.isRecordingSessionType || this.isVideoSessionType) && this.$store.getters['user/currentUserSubscriptionActive']()) {
        menuItems.push({
          name: 'TTS Voices',
          icon: TTSIcon,
          description: 'Set the playback language and voice for the text-to-speech feature.',
          slotFunctionality: true
        });
      }

      if (this.audioComponent || this.videoComponent) {
        menuItems.push({
          name: 'Auto Scroll',
          icon: AutoScrollIcon,
          description: 'Transcript follows the audio, keeping the current section in view as it plays.',
          checked: this.autoScrollEnabled,
          disabled: false,
          endOfSection: true,
          onToggle: () => {
            this.autoScrollEnabled = !this.autoScrollEnabled;
            localStorage.setItem("autoScrollEnabled", this.autoScrollEnabled.toString());
          }
        });
      }

      // Show this menu item only if it is one of the allowed session and pro notes already doesn't exist
      if ((this.audioComponent || this.videoComponent || this.pdfSession) && !this.canShowProNote && this.hasProNoteAccess) {
        menuItems.push({
          name:  this.proNoteRequested ? 'Notes Requested' : 'Request Pro Notes',
          icon: RequestProNoteIcon,
          disabled: this.proNoteRequested,
          onClick: () => {
            this.orderFormVisible = true;
          }
        });
      }

      if (!this.isServiceProvider) {
        menuItems.push({
          name: `Add supplementary materials (${this.additionalUploads.length}/5)`,
          icon: UploadWhiteIcon,
          disabled: this.additionalUploads.length > 4,
          onClick: () => this.addSupplementaryMaterials(),
        });
      }

      return menuItems;
    },

    ttsVoices() {
      const voiceAccent = this.accents.find(accent => accent.name === this.selectedAccent).key;
      return this.voiceList
      .filter(voice => voiceAccent === 'all' || voice.accents.includes(voiceAccent))
      .map((voice) => ({
        name: voice.name,
        description: voice.description,
        status: voice.status,
        onClick: () => {
          this.ttsVoiceSelected(voice)
        }
      }))
    },

    hasProNoteAccess() {
      return this.$store.getters['user/hasProNotesEnabled'];
    },

    additionalUploads() {
      return this.$store.state.user.sessionDetails.uploads.filter(
        (upload) =>
          upload.type === "mp-supplementary" && upload.deletedAt === null
      );
    },

    sessionId() {
      return this.$store.state.user.sessionDetails.id;
    },

    isNewVoiceProcessing() {
      return this.$store.state.user.sessionDetails.isProcessing;
    },

    transcriptAvailable() {
      const transcriptData = this.$store.state.user.sessionDetails.transcriptData;
      const hasTranscript = transcriptData && typeof transcriptData === 'object' && Object.keys(transcriptData).length > 0;

      if (this.isRecordingSessionType || this.isVideoSessionType) {
        return hasTranscript;
      }

      return true;
    },

    actions() {
      return [
        {
          type: 'group',
          name: 'Show AI tools',
          children: [
            {
              type: 'heading',
              name: 'Study Assisstant',
              image: MagicWandPinkIcon,
            },
            {
              type: 'action',
              name: 'Define',
              image: DefineTextIcon,
              onclick: (editor) => this.checkAndProcessText('Define', editor)
            },
            {
              type: 'action',
              name: 'Simplify',
              image: SimplifyTextIcon,
              onclick: (editor) => this.checkAndProcessText('Simplify', editor)
            },
            {
              type: 'action',
              name: 'Rewrite',
              image: RewriteTextIcon,
              onclick: (editor) => this.checkAndProcessText('Rewrite', editor)
            },
            {
              type: 'action',
              name: 'Proofread',
              image: ProofreadTextIcon,
              onclick: (editor) => this.checkAndProcessText('Proofread', editor)
            }
          ]
        },
        {
          type: 'group',
          name: 'Bookmark tools',
          children: [
            {
              type: 'colors',
              name: '',
              children: [
              {
                  type: 'action',
                  name: '#FFD362',
                  onclick: () => {this.colourButtonClicked('#FFD362');}
                },
                {
                  type: 'action',
                  name: '#FFA69A',
                  onclick: () => {this.colourButtonClicked('#FFA69A');}
                },
                {
                  type: 'action',
                  name: '#79DCFF',
                  onclick: () => {this.colourButtonClicked('#79DCFF');}
                },
                {
                  type: 'action',
                  name: '#02FADA',
                  onclick: () => {this.colourButtonClicked('#02FADA');}
                },
                {
                  type: 'action',
                  name: '#E993FF',
                  onclick: () => {this.colourButtonClicked('#E993FF');}
                }
              ]
            },
            {
              type: 'action',
              name: 'Delete bookmark',
              image: DeleteIcon,
              onclick: this.deleteBookmark
            }
          ]
        },
        {
          type: 'action',
          name: 'Copy',
          image: CopyIcon,
          onclick: (editor) => this.checkForBookmarkAndRedirect('copy', editor)
        },
        {
          type: 'action',
          name: 'Bookmark',
          image: BookmarkIcon,
          onclick: this.bookmarkText
        },
        {
          type: 'action',
          name: 'Add to notes',
          image: AddToNotesIcon,
          onclick: (editor) => this.checkForBookmarkAndRedirect('notes', editor)
        },
        {
          type: 'action',
          name: 'Read',
          image: ReadAloudIcon,
          isDisabled: this.isNewVoiceProcessing,
          disabledReason: this.isNewVoiceProcessing ? 'The option will be available once the new audio is ready.' : '',
          onclick: this.readText
        }
      ]
    },
  },

  async created() {
    this.resizeHandler = _.debounce(this.resizeHandler, 20);
  },

  mounted() {
    if (this.$store.state.user.sessionDetails.title) {
      this.mediaName = this.$store.state.user.sessionDetails.title;
    } else if (this.$store.state.user.sessionDetails.mediaUploadData) {
      this.mediaName = this.$store.state.user.sessionDetails.mediaUploadData.id;
    } else {
      this.mediaName = "No title";
    }

    if (this.isDocumentSessionType) {
      // Document sessions don't need to load audio or summary data
      this.initializePdfData(this.$store.state.user.sessionDetails.documentUrl);
    } else if (!this.transcriptAvailable) {
      this.initializeProcessingState();
    } else if (this.videoSession) {
      const voiceMap = this.$store.state.user.ttsVoiceBySession;
      if (voiceMap[this.sessionId] !== "default") {
        this.hideVideoAndInitialiseAudioPlayer();
      }
      this.initializeVideoData();
      this.getSummariseData();
    } else {
      if (this.isRecordingSessionType) {
        this.initializeRecordingSession();
      } else {
        this.getAudio();
      }
      this.getSummariseData();
    }

    this.backupNotesInterval = setInterval(() => {
      this.backupNotes();
    }, 10000);

    this.formatedInfoPanelSessionData = [];

    // SP side doesn't show session type so sessionType will be undefined
    if (this.$store.state.user.sessionDetails.sessionType) {
      this.formatedInfoPanelSessionData.push({
        title: "Session type",
        info: [
          {
            text:
              this.$store.state.user.sessionDetails.sessionType[0].toUpperCase() +
              this.$store.state.user.sessionDetails.sessionType.slice(1),
            type: "primary",
          },
        ],
      });
    }

    this.formatedInfoPanelSessionData.push(
      {
        title: "Last modified",
        info: [
          {
            text: formatDate(
              this.$store.state.user.sessionDetails.updatedAt ||
                // Beehive and MP server format their data slightly differently
                this.$store.state.user.sessionDetails.updated_at,
              this.$store.state.user.sessionDetails.updatedAt ||
                this.$store.state.user.sessionDetails.updated_at,
              null,
              false,
              true
            ),
            type: "primary",
          },
        ],
      },
      {
        title: "Created",
        info: [
          {
            text: formatDate(
              this.$store.state.user.sessionDetails.createdAt ||
                this.$store.state.user.sessionDetails.created_at,
              this.$store.state.user.sessionDetails.createdAt ||
                this.$store.state.user.sessionDetails.created_at,
              null,
              false,
              true
            ),
            type: "primary",
          },
        ],
      }
    );
  },

  unmounted() {
    clearInterval(this.backupNotesInterval);
  },

  methods: {
    /**** PDF Viewer methods ****/
    initializePdfData(data) {
      this.pdfData = data;
    },

    handlePdfRender() {
      if (this.isInitialPdfRender) {
        this.pdfViewerNumPages = this.$refs.pdfViewer.pageCount;
        this.pdfViewerCurrentPage = 1;
        this.isInitialPdfRender = false;
        this.loading = false;
      }

      // Have to reset scale and width calculations on re-render in case window width changed
      setTimeout(() => {
        // Small delay to let the vue-embed-pdf fully render before calculating width
        this.pdfViewerInitialWidth = this.$refs.pdfViewer.$el.clientWidth;
      }, 200);

      this.pdfViewerScale = 1;
    },

    resizeHandler() {
      this.pdfViewerScale =
        (this.$refs.pdfPanel.clientWidth - 80) / this.pdfViewerInitialWidth;
    },

    switchToPdfPage(page) {
      if (page < 1) {
        this.pdfViewerCurrentPage = 1;
      } else if (page > this.pdfViewerNumPages) {
        this.pdfViewerCurrentPage = this.pdfViewerNumPages;
      } else {
        this.pdfViewerCurrentPage = page;
      }
    },

    setPdfOverviewPanelVisible(bool) {
      this.pdfOverviewPanelVisible = bool;
    },
    /**** End of PDF Viewer methods ****/

    /**** Video Ssession methods ****/
    initializeVideoData() {
      this.setTranscriptText();
    },

    onMountedVideoPlayer(videoElementParameter) {
      const { video } = videoElementParameter;
      this.videoComponent = video;
      this.loading = false;
      this.videoReady = true;
      if (this.videoState) {
        video.currentTime = this.videoState.currentTime;
        if (this.videoState.paused) {
          video.pause();
        } else {
          video.play();
        }
      }

      if (this.videoDuration < 0) {
        video.addEventListener("loadedmetadata", () => {
          this.videoDuration = this.videoComponent.duration * 1000;
          this.$refs.audioVisualizer.updateBookmarkList(this.bookmarkList);
          const durationString = convertMillisecondsToHMS(
            this.videoDuration
          ).split(":");
          const durationFormated =
            `${durationString[0] !== "00" ? durationString[0] + " hr " : ""}` +
            `${durationString[1] !== "00" ? durationString[1] + " min " : ""}` +
            `${durationString[2]} sec`;
          this.formatedInfoPanelSessionData.push({
            title: "Duration",
            info: [
              {
                text: durationFormated,
                type: "primary",
              },
            ],
          });
        });
      }
      this.loading = false;
    },

    hideVideoAndInitialiseAudioPlayer() {
      this.hideVideoPlayer = true;
      this.setAudioComponent(this.$store.state.user.sessionDetails.videoUrl);
    },

    toggleVideoPosition() {
      // save video state before
      this.videoState = {};
      this.videoState.currentTime = this.videoComponent.currentTime;
      this.videoState.paused = this.videoComponent.paused;
      this.videoComponent.pause();
      this.videoComponent = null;
      this.videoElementDestination =
        this.videoElementDestination === "above-transcript"
          ? "above-personal-note"
          : "above-transcript";
      this.videoSession = false;
      setTimeout(() => {
        this.videoSession = true;
      }, 100);
    },

    // collapse video tile when video enters PIP mode
    setToggleVideoTile(val) {
      this.toggleVideoTile = val;
    },

    setPIPMode() {
      if (this.videoComponent && this.videoComponent.requestPictureInPicture) {
        this.videoComponent.requestPictureInPicture();
      }
    },
    /**** End of Video Session methods ****/

    updateTimestampFrequency(n) {
      this.transcriptTimestampFrequency = n;
    },

    async setTranscriptText() {
      // Get token, bookmarks and voices
      await this.getBookmarks();
      await this.checkTiptapToken();
      await this.getVoices();

      const transcriptData = this.$store.state.user.sessionDetails.transcriptData;
      const captionLines = transcriptData?.sentences;
      const rawData = transcriptData?.rawData;

      // If no transcript data return empty transcript
      if (!captionLines) {
        this.savedTranscript = "";
        return;
      }

      // Pre-compute timestamp conversion cache
      const timestampCache = new Map();
      const getFormattedTimestamp = (timestamp) => {
        if (!timestampCache.has(timestamp)) {
          const durationString = convertMillisecondsToHMS(timestamp).split(":");
          const formattedDuration = `${durationString[0] !== "00" ? durationString[0] + ":" : ""}` + `${durationString[1]}:${durationString[2]}`
          timestampCache.set(timestamp, formattedDuration);
        }
        return timestampCache.get(timestamp);
      };

      // Group words by sentence index for efficient lookup
      const wordsBySentence = new Map();
      if (rawData) {
        for (const word of rawData) {
          if (!wordsBySentence.has(word.sentenceIndex)) {
            wordsBySentence.set(word.sentenceIndex, []);
          }
          wordsBySentence.get(word.sentenceIndex).push(word);
        }
      }

      // Bookmark lookup map by line index
      const bookmarksByLine = new Map();

      // Map bookmarks to transcript
      this.bookmarkList.forEach((bookmark) => {
        let lineIndex;

        if (bookmark.wordIndex !== null) {
          lineIndex =  captionLines.findIndex(line => line.startIndex <= bookmark.wordIndex && line.endIndex >= bookmark.wordIndex);
        } else {
          lineIndex =  captionLines.findIndex(line => line.startTimestamp <= bookmark.timestamp && line.endTimestamp >= bookmark.timestamp) - 1;
        }

        // Handle edge case where bookmark is not found
        if (lineIndex < 0 ) {
          lineIndex = captionLines[0].startTimestamp > bookmark.timestamp ? 0 : captionLines.length - 1;
        }

        // Group bookmarks by line index
        if (!bookmarksByLine.has(lineIndex)) {
          bookmarksByLine.set(lineIndex, []);
        }
        bookmarksByLine.get(lineIndex).push(bookmark);

        // Update bookmark properties
        bookmark.text = captionLines[lineIndex].sentence;
        bookmark.paragraphTimestamp = captionLines[lineIndex].startTimestamp;
        bookmark.paragraphIndex = lineIndex;
      });

      // Update bookmarkTracker
      this.bookmarkTracker = {};
      bookmarksByLine.forEach((bookmarks, lineIndex) => {
        this.bookmarkTracker[lineIndex] = bookmarks;
      });

      // Create transcript HTML
      const htmlParts = [];
      captionLines.forEach((element, index) => {
        const bookmarks = bookmarksByLine.get(index);

        const words = wordsBySentence.get(element.sentenceIndex) || [];

        let text = words.map(word => {
          const wordText = `${word.word.text}`.concat(" ");
          return `<span id="${index}-${word.wordIndex}">${wordText}</span>`;
        }).join("");

        // Apply bookmark highlighting if exists
        if (bookmarks && bookmarks.length > 0) {
          const bookmark = bookmarks[bookmarks.length - 1];
          bookmarks[bookmarks.length - 1].text = text;
          text = `<mark data-color="${HIGHLIGHT_COLOR_MAP[bookmark.color]}">${text}</mark>`;
        }

        const speakerId = element.speakerId;
        let speaker = null;
        let header = '';

        if (speakerId) {
          const speakerIndex = speakerId.indexOf('_');
          speaker = speakerIndex !== -1 ? (parseInt(speakerId.slice(speakerIndex + 1)) + 1).toString() : '1';
          header = `Speaker ${speaker}`;
          this.speakerEnabled = true;
        }

        if (this.timestampEnabled) {
          const formattedTime = getFormattedTimestamp(element.startTimestamp);
          header += this.speakerEnabled ? ` <span class="timestamp">${formattedTime}</span>` : `${formattedTime}`;
        }

        // Add timestamp and speaker if enabled
        if (this.timestampEnabled || this.speakerEnabled) {
          htmlParts.push(
            `<p id="${index}-header">${header}</p>`,
            `<p id="${index}" class="${index}">${text}</p>`
          );
        } else {
          htmlParts.push(`<p id="${index}" class="${index}">${text}</p>`);
        }
      });

      this.savedTranscript = htmlParts.join(" ");

      if (this.isNewVoiceProcessing) {
        this.checkVoiceAvailable();
      }
    },

    editBookmarkContent(timeStamp, content) {
      const transcriptHTML = this.transcriptEditor.getHTML();
      const captionLines = this.$store.state.user.sessionDetails.transcriptData?.sentences;

      const parser = new DOMParser();
      const htmlDoc = parser.parseFromString(transcriptHTML, "text/html");
      const selectedP = htmlDoc.getElementsByTagName("p");
      const paragraphIndex = captionLines.findIndex((line) => {
          return line.startTimestamp <= timeStamp && line.endTimestamp >= timeStamp;
        });
      const paragraph = [...selectedP].find((element) => {
        return element.id === paragraphIndex.toString();
      });
      paragraph.innerHTML = content;
      this.savedTranscript = htmlDoc.documentElement.outerHTML;
      this.transcriptEditor.commands.setContent(this.savedTranscript);
    },

    addBookmarkListAudioVisualizer(open) {
      open && this.$refs.audioVisualizer.updateBookmarkList(this.bookmarkList);
    },

    initializeRecordingSession() {
      // Load transcript
      this.setTranscriptText();

      // Load recording audio
      this.setAudioComponent(this.$store.state.user.sessionDetails.audioUrl);
    },

    setAudioComponent(audioUrl) {
      this.audioComponent = new Audio();
      this.audioComponent.src = audioUrl;
      this.audioComponent.addEventListener("loadedmetadata", () => {
        this.audioDuration = this.audioComponent.duration * 1000;
      });
      this.audioComponent.addEventListener("play", () => {
        this.startPlaying();
      });
      this.audioComponent.addEventListener("pause", () => {
        this.pausePlaying();
      });
      this.audioComponent.addEventListener("timeupdate", () => {
        this.playing && this.highlightCurrentText();
      });
      this.loading = false;
    },

    async getAudio() {
      try {
        await this.checkTiptapToken();
        if (this.$store.state.user.sessionDetails.bundledCaptioningId) {
          // Bundled session, so get the audio & transcript stored in S3
          const getAudioParams = {
            Key: "Lecture-example-right-hand-rule.mp3",
            Bucket: process.env.VUE_APP_AWS_NOTETAKING_BUCKET,
            Expires: 86400,
          };

          const getTranscriptParams = {
            Key:
              this.$store.state.user.sessionDetails.bundledCaptioningId +
              dbConstants.CAPTIONING_TRANSCRIPT_S3_FILEPATH,
            Bucket: process.env.VUE_APP_AWS_NOTETAKING_BUCKET,
          };

          const s3 = new AWS.S3({
            region: "us-east-2",
            accessKeyId: process.env.VUE_APP_AWS_ACCESS_KEY_ID,
            secretAccessKey: process.env.VUE_APP_AWS_SECRET_ACCESS_KEY,
          });

          let getAudioPromise = new Promise((resolve) => {
            const presignedAudioUrl = s3.getSignedUrl(
              "getObject",
              getAudioParams
            );

            this.audioComponent = new Audio();
            this.audioComponent.src = presignedAudioUrl;
            this.audioComponent.addEventListener("loadedmetadata", () => {
              this.audioDuration = this.audioComponent.duration * 1000;
            });
            resolve("Audio loaded");
          });

          let getTranscriptPromise = new Promise((resolve, reject) => {
            s3.getObject(getTranscriptParams, (err, data) => {
              this.savedTranscript = JSON.parse(data.Body);

              resolve("Transcript loaded");
            });

            setTimeout(() => {
              reject("Request timeout");
            }, 10000);
          });

          Promise.all([getAudioPromise, getTranscriptPromise]).then(() => {
            this.loading = false;
          });
        } else {
          // Not bundled, so load transcript/audio from anthill

          this.savedTranscript = "";
          const anthillApiHeaders = {
            API_KEY: process.env.VUE_APP_ANTHILL_KEY,
          };

          const anthillEndpoint =
            process.env.VUE_APP_ANTHILL_TRANSCRIPT_API +
            this.$store.state.user.sessionDetails.anthillTranscriptId;

          const beehiveFalconEndpoint =
            "https://beehive.habitatlearn.com/api/falcon_link?id=" +
            this.$store.state.user.sessionDetails.id +
            "&source=mp";

          let getAudioPromise = new Promise((resolve, reject) => {
            axios
              .get(beehiveFalconEndpoint, {
                withCredentials: false,
              })
              .then((res) => {
                this.audioComponent = new Audio();
                this.audioComponent.src = res.data[0].audio_file.url;
                this.audioComponent.addEventListener("loadedmetadata", () => {
                  this.audioDuration = this.audioComponent.duration * 1000;
                });
                resolve("Audio loaded");
              })
              .catch((error) => {
                console.error(error);
                reject("Request error");
              });
            setTimeout(() => {
              reject("Request timeout");
            }, 15000);
          });

          let getTranscriptPromise = new Promise((resolve, reject) => {
            axios
              .get(anthillEndpoint, {
                headers: anthillApiHeaders,
                withCredentials: false,
              })
              .then((res) => {
                const rawTranscriptData = JSON.parse(res.data.content.raw);
                let transcriptText = "";
                for (let i = 0; i < rawTranscriptData.length; i++) {
                  transcriptText =
                    transcriptText + " " + rawTranscriptData[i].text;
                }
                this.savedTranscript = transcriptText;
                resolve("Transcript loaded");
              })
              .catch((err) => {
                console.error(err);
                reject("Request error");
              });
            setTimeout(() => {
              reject("Request timeout");
            }, 10000);
          });

          Promise.all([getAudioPromise, getTranscriptPromise])
            .then(() => {
              this.loading = false;
            })
            .catch(() => {
              this.loading = false;
              this.errorLoading = true;
              this.errorLoadingMessage = "Error loading transcript/audio";
            });
        }
      } catch (error) {
        console.error(error);
        this.loading = false;
        this.errorLoading = true;
        this.errorLoadingMessage = "Error retrieving transcript/audio";
      }
    },

    async getSummariseData() {
      if (
        this.$store.state.user.sessionDetails.mediaUploadData &&
        this.$store.state.user.sessionDetails.mediaUploadData.id
      ) {
        const summariseEndpoint =
          process.env.VUE_APP_MP_SERVER_API_URL +
          `self/openai/${this.$store.state.user.sessionDetails.mediaUploadData.id}/summarise`;
        try {
          const summariseResponse = await axios.get(summariseEndpoint, {
            withCredentials: false,
            headers: {
              Authorization:
                "Bearer " +
                JSON.parse(localStorage.getItem("tokenMP")).accessToken,
            },
          });
          if (
            summariseResponse.status !== 200 ||
            summariseResponse.data.status !== "success"
          ) {
            console.error("Could not retrieve MP data");
          } else {
            const summariseData = summariseResponse.data.data;
            this.summariseQuizList = summariseData.quiz;
            this.summariseKeywordsList = summariseData.keywords;
            this.summariseRecommendationsList = summariseData.furtherReading;
            this.summariseAbstract = summariseData.abstract;
          }
        } catch (error) {
          console.error("Could not retrieve MP data");
        }
      }
    },

    insertCurrentTimestamp() {
      let state = this.noteEditor.state;
      const dispatch = this.noteEditor.view.dispatch;

      let selectionHead = state.selection.$head.pos;
      let selectionAnchor = state.selection.$anchor.pos;

      const timestamp = getTimeCodeFromNum(this.audioComponent.currentTime);

      this.noteEditor
        .chain()
        .focus()
        .selectTextblockStart()
        .setHighlight()
        .insertContent(timestamp)
        .run();

      this.noteEditor.chain().focus().unsetHighlight().insertContent(" ").run();

      state = this.noteEditor.state;

      // The total size of inserted text
      const shiftAmount = timestamp.length + 1;

      const selection = new TextSelection(
        state.doc.resolve(selectionHead + shiftAmount),
        state.doc.resolve(selectionAnchor + shiftAmount)
      );

      let transaction = state.tr.setSelection(selection);
      if (dispatch) dispatch(transaction);
    },

    showNotesEmoji(emoji) {
      this.noteEditor.chain().focus().setEmoji(emoji.id).run();
    },

    setNotesZoomFactor(zoomFactor) {
      this.zoomFactorNotes = zoomFactor;
    },

    addNotesImage() {
      const url = window.prompt("URL");

      if (url) {
        this.noteEditor.chain().focus().setImage({ src: url }).run();
      }
    },

    saveSessionName() {
      this.sessionCurrentName = this.sessionPreviewName;
      this.$refs.sessionNameMenu.hide();
    },

    cancelSessionName() {
      this.$refs.sessionNameMenu.hide();
    },

    toggleModifySessionNameMenu(event) {
      this.$refs.sessionNameMenu.toggle(event);
    },

    /******* Study Assistant methods *******/
    pushToStudyAssistantQuestionsAnswersList(item) {
      this.studyAssistantQuestionsAnswersList.push(item);
    },
    /******* End of Study Assistant methods ********/

    saveToComputer(panelToSave) {
      let editorToSave;
      let typeSuffix;
      if (panelToSave === this.PANEL_TYPES.TRANSCRIPT) {
        editorToSave = this.transcriptEditor;
        typeSuffix = "Transcript";
      } else {
        if (this.showProNotes) {
          editorToSave = this.proNoteEditor;
          typeSuffix = "ProNotes";
        } else {
          editorToSave = this.noteEditor;
          typeSuffix = "PersonalNotes";
        }
      }

      const docHTML = editorToSave.getHTML();

      downloadFile(docHTML, this.mediaName, typeSuffix);
    },

    // Backup notes to localstorage
    backupNotes() {
      if (!this.noteEditor) {
        // Before noteEditor is initialized
        return;
      }
      let savedNotes = JSON.parse(localStorage.getItem("savedNotes"));
      if (!savedNotes) {
        savedNotes = {};
      }
      savedNotes[this.$store.state.user.sessionDetails.id] =
        this.noteEditor.getJSON();
      localStorage.setItem("savedNotes", JSON.stringify(savedNotes));
    },

    setTranscriptEditor(transcriptEditor) {
      this.transcriptEditor = transcriptEditor;
    },

    async retrieveOrderId() {
      let myHeaders = new Headers();
      myHeaders.append(
        "Authorization",
        `Bearer ${JSON.parse(localStorage.getItem("tokenMP")).accessToken}`
      );

      let requestOptions = {
        method: "GET",
        headers: myHeaders,
        redirect: "follow",
      };

      const response = await fetch(
        process.env.VUE_APP_MP_SERVER_API_URL +
          `self/sessions/${this.$store.state.user.sessionDetails.id}`,
        requestOptions
      );

      const sessionData = await response.json();

      // Filter for valid order and prioritize it over invalid orders
      const nonErrorOrder = sessionData.data.orders.find(
        (order) => !ORDER_STATUS.ERROR.includes(order.orderStatus)
      );
      const errorOrder = sessionData.data.orders.find((order) =>
        ORDER_STATUS.ERROR.includes(order.orderStatus)
      );

      return nonErrorOrder
        ? nonErrorOrder.id
        : errorOrder
        ? errorOrder.id
        : null;
    },

    async downloadOrderIdJSONText(orderId) {
      let myHeaders = new Headers();
      myHeaders.append(
        "Authorization",
        `Bearer ${JSON.parse(localStorage.getItem("tokenMP")).accessToken}`
      );

      let requestOptions = {
        method: "GET",
        headers: myHeaders,
        redirect: "follow",
      };

      const response = await fetch(
        process.env.VUE_APP_MP_SERVER_API_URL +
          `self/orders/${orderId}/download`,
        requestOptions
      );

      const orderText = await response.json();

      return orderText;
    },

    setHighlightedFontSize(fontSize) {
      this.highlightedFontSize = fontSize;
    },

    async setNoteEditor(noteEditor, isProNote=false) {
      if (isProNote) {
        console.log('[DEBUG] aaaaaaaaaaaaaaaaa')
        this.proNoteEditor = noteEditor;
        this.proNoteEditor.on("selectionUpdate", ({ editor }) => {
          this.highlightedFontSize = editor.getAttributes("textStyle").fontSize;
        });
      } else {
        this.noteEditor = noteEditor;
        this.noteEditor.on("selectionUpdate", ({ editor }) => {
          this.highlightedFontSize = editor.getAttributes("textStyle").fontSize;
        });
      }
    },

    async setProNoteEditor(proNoteEditor) {
      this.proNoteEditor = proNoteEditor;
      let noteEditorContent = "";
      let orderId;

      // Order was set and passed through from Standby table
      if (this.$store.state.user.sessionDetails.order) {
        orderId = this.$store.state.user.sessionDetails.order.id;
      } else {
        try {
          orderId = await this.retrieveOrderId();
        } catch {
          (err) => {
            console.log(err);
          };
        }
      }

      let orderText;
      if (!orderId) {
        this.$store.commit("user/setSessionDetails", {
          ...this.$store.state.user.sessionDetails,
          status: "no-value",
        });
      } else {
        orderText = await this.downloadOrderIdJSONText(orderId);
        if (orderText.data) {
          const raw = JSON.stringify({
            tiptapJSON: JSON.parse(orderText.data.notes),
          });

          const html = await makeMPServerRequest(
            "utils/convert-tiptap-json-to-html",
            "POST",
            "text",
            raw
          );

          noteEditorContent = html;
        } else {
          this.$store.commit("user/setSessionDetails", {
            ...this.$store.state.user.sessionDetails,
            status: "no-value",
          });
        }
      }

      // //convertSecondsToMS
      this.proNoteEditor.commands.setContent(noteEditorContent);
    },

    pasteToNote(text) {
      try {
        this.noteEditor.chain().focus().insertContent(text).run();
      } catch (e) {
        console.error(e);
      }
    },

    togglePausePlayAudio() {
      if (this.audioComponent.paused) {
        this.audioComponent.play();
        this.audioPaused = false;
      } else {
        this.audioComponent.pause();
        this.audioPaused = true;
      }
    },

    skipBackward(amt) {
      this.audioComponent.currentTime = this.audioComponent.currentTime - amt;
    },

    skipForward(amt) {
      this.audioComponent.currentTime = this.audioComponent.currentTime + amt;
    },

    volumeUp(amt) {
      this.audioComponent.volume = Math.min(
        1,
        this.audioComponent.volume + amt
      );
      this.audioVolume = this.audioComponent.volume;
    },

    volumeDown(amt) {
      this.audioComponent.volume = Math.max(
        0,
        this.audioComponent.volume - amt
      );
      this.audioVolume = this.audioComponent.volume;
    },

    slowDownPlaybackSpeed(amt) {
      this.audioComponent.playbackRate = Math.max(
        0.25,
        this.audioComponent.playbackRate - amt
      );
      this.playbackSpeed = this.audioComponent.playbackRate;
    },

    speedUpPlaybackSpeed(amt) {
      this.audioComponent.playbackRate = Math.min(
        2,
        this.audioComponent.playbackRate + amt
      );
      this.playbackSpeed = this.audioComponent.playbackRate;
    },

    setPlaybackSpeed(val) {
      this.audioComponent.playbackRate = val;
      this.playbackSpeed = this.audioComponent.playbackRate;
    },

    handleKeypress(ev) {
      if (ev.key === " ") {
        this.togglePausePlayAudio();
      } else if (ev.key === "ArrowLeft") {
        if (ev.shiftKey) {
          this.skipBackward(this.AUDIO_SKIP_AMOUNT_MEDIUM);
        } else if (ev.ctrlKey || ev.metaKey) {
          this.skipBackward(this.AUDIO_SKIP_AMOUNT_LARGE);
        } 
        // else {
        //   this.skipBackward(this.AUDIO_SKIP_AMOUNT_SMALL);
        // }
      } else if (ev.key === "ArrowRight") {
        if (ev.shiftKey) {
          this.skipForward(this.AUDIO_SKIP_AMOUNT_MEDIUM);
        } else if (ev.ctrlKey || ev.metaKey) {
          this.skipForward(this.AUDIO_SKIP_AMOUNT_LARGE);
        } 
        // else {
        //   this.skipForward(this.AUDIO_SKIP_AMOUNT_SMALL);
        // }
      } else if (ev.key === "ArrowUp") {
        if (ev.ctrlKey || ev.metaKey) {
          this.volumeUp(this.VOLUME_ADJUST_AMOUNT);
        }
      } else if (ev.key === "ArrowDown") {
        if (ev.ctrlKey || ev.metaKey) {
          this.volumeDown(this.VOLUME_ADJUST_AMOUNT);
        }
      } else if (ev.key === "-") {
        this.slowDownPlaybackSpeed(this.PLAYBACK_SPEED_ADJUST_AMOUNT);
      } else if (ev.key === "=") {
        this.speedUpPlaybackSpeed(this.PLAYBACK_SPEED_ADJUST_AMOUNT);
      }
    },


    toggleInfoPanelOpen() {
      this.infoPanelOpen = !this.infoPanelOpen;

      // If the user manually toggles the info panel, don't want to save this state
      this.shouldCloseInfoPanel = false;
    },
    toggleStudyAssistantPanel() {
      this.isStudyAssistantOpen = !this.isStudyAssistantOpen;
    },

    toggleTranscriptOpen() {
      this.isTranscriptOpen = !this.isTranscriptOpen;
    },

    closeTranscriptPanel() {
      this.isTranscriptOpen = false;
    },

    openTranscriptPanel() {
      this.isTranscriptOpen = true;
    },

    toggleNotepadOpen() {
      this.isNotepadOpen = !this.isNotepadOpen;
    },

    closeNotepadPanel() {
      this.isNotepadOpen = false;
    },

    openNotepadPanel() {
      this.isNotepadOpen = true;
    },

    toggleSidePanelOpen() {
      this.isSidePanelOpen = !this.isSidePanelOpen;
    },

    goToSessionBrowser() {
      this.$router.push("/home");
    },

    changeFontColourTranscript(hex) {
      this.fontColourTranscript = hex;
    },

    toggleFontBoldTranscript() {
      this.fontBoldTranscript = !this.fontBold;
    },

    changeFontFamilyTranscript(family) {
      this.fontFamilyTranscript = family;
    },

    changeFontSizeTranscript(size) {
      this.fontSizeTranscript = size;
    },

    changeLineHeightTranscript(lineHeight) {
      this.lineHeightTranscript = Math.round(lineHeight * 10) / 10;
    },

    changeLetterSpacingTranscript(spacing) {
      this.letterSpacingTranscript = spacing;
    },

    setShowSubscriptionSignupDialog(bool) {
      this.showSubscriptionSignupDialog = bool;
    },

    setOrderFormVisible(val) {
      this.orderFormVisible = val;
    },

    updateProNoteRequest() {
      this.$store.commit("user/setSessionDetails", {
        ...this.$store.state.user.sessionDetails,
        status: "requested",
      });
    },

    closeProNoteAlert() {
      this.showProNoteAlert = false;
    },
    updateTitle(newTitle) {
      this.mediaName = newTitle;
      this.$store.commit("user/setSessionDetails", {
        ...this.$store.state.user.sessionDetails,
        title: newTitle,
      });
    },

    /// Bookmark methods
    async colourButtonClicked(color) {
      const index =
        this.transcriptEditor.state.selection.$anchor.parent.attrs.id;
      const bookmark = this.bookmarkList.find((bookmark) => {
        return bookmark.paragraphIndex === parseInt(index);
      });
      if (color === bookmark.color) {
        return;
      }
      bookmark.color = color;
      let myHeaders = new Headers();
      myHeaders.append(
        "Authorization",
        `Bearer ${JSON.parse(localStorage.getItem("tokenMP")).accessToken}`
      );
      myHeaders.append("Content-Type", "application/json");

      let raw = JSON.stringify({
        color: color,
      });

      let requestOptions = {
        method: "PUT",
        headers: myHeaders,
        body: raw,
        redirect: "follow",
      };

      const response = await fetch(
        process.env.VUE_APP_MP_SERVER_API_URL +
          `self/uploads/${this.$store.state.user.sessionDetails.mediaUploadData.id}/` +
          `bookmarks/${bookmark.id}`,
        requestOptions
      );
      const bookmarkData = await response.json();
      if (bookmarkData.status === "success") {
        this.changeBookmarkColor(bookmark);
      }
    },

    async getBookmarks() {
      let myHeaders = new Headers();
      myHeaders.append(
        "Authorization",
        `Bearer ${JSON.parse(localStorage.getItem("tokenMP")).accessToken}`
      );
      myHeaders.append("Content-Type", "application/json");

      let requestOptions = {
        method: "GET",
        headers: myHeaders,
        redirect: "follow",
      };

      const response = await fetch(
        process.env.VUE_APP_MP_SERVER_API_URL +
          `self/uploads/${this.$store.state.user.sessionDetails.mediaUploadData.id}/bookmarks`,
        requestOptions
      );
      const bookmarkData = await response.json();
      this.bookmarkList = bookmarkData.data;
    },

    removeBookmark(bookmark) {
      // Find index and remove bookmark from bookmarkList and sessionDetails
      const index = this.bookmarkList.indexOf(bookmark);
      this.bookmarkList.splice(index, 1);
      this.$store.commit("user/setSessionDetails", {
        ...this.$store.state.user.sessionDetails,
        bookmarks: this.bookmarkList,
      });

      // Update audioVisualizer
      this.$refs.audioVisualizer?.updateBookmarkList(this.bookmarkList);

      // If no bookmark found return
      if (bookmark.paragraphIndex < 0) {
        return;
      }

      // check if there are other bookmarks on the paragraph
      const bookmarks = this.bookmarkTracker[bookmark.paragraphIndex];
      const bookmarkIndex = bookmarks.indexOf(bookmark);
      bookmarks.splice(bookmarkIndex, 1);

      // Update transcript HTML
      let text = bookmark.text;
      if (bookmarks.length !== 0) {
        const nextBookmark = bookmarks[bookmarks.length - 1];

        text = `<mark data-color="${HIGHLIGHT_COLOR_MAP[nextBookmark.color]}">
            ${text}
          </mark>`;
      }
      const transcriptHTML = this.transcriptEditor.getHTML();
      const parser = new DOMParser();
      const htmlDoc = parser.parseFromString(transcriptHTML, "text/html");
      const selectedP = htmlDoc.getElementsByTagName("p");
      const paragraphIndex = bookmark.paragraphIndex;

      const paragraph = [...selectedP].find((element) => {
        return element.id === paragraphIndex.toString();
      });
      if (!paragraph) {
        return;
      }

      paragraph.innerHTML = `${text}`;
      this.savedTranscript = htmlDoc.documentElement.outerHTML;
      this.transcriptEditor.commands.setContent(this.savedTranscript);
    },

    changeBookmarkColor(bookmark) {
      this.$store.commit("user/setSessionDetails", {
        ...this.$store.state.user.sessionDetails,
        bookmarks: this.bookmarkList,
      });

      // Update audioVisualizer
      this.$refs.audioVisualizer?.updateBookmarkList(this.bookmarkList);

      // If no bookmark found return
      if (bookmark.paragraphIndex < 0) {
        return;
      }

      // Check if there are other bookmarks on the paragraph
      const bookmarks = this.bookmarkTracker[bookmark.paragraphIndex];
      const bookmarkIndex = bookmarks.indexOf(bookmark);
      bookmarks.splice(bookmarkIndex, 1);

      // Update transcript HTML
      const transcriptHTML = this.transcriptEditor.getHTML();
      const parser = new DOMParser();
      const htmlDoc = parser.parseFromString(transcriptHTML, "text/html");
      const selectedP = htmlDoc.getElementsByTagName("p");
      const paragraphIndex = bookmark.paragraphIndex;

      const paragraph = [...selectedP].find((element) => {
        return element.id === paragraphIndex.toString();
      });

      if (!paragraph) {
        return;
      }

      paragraph.innerHTML = `<mark data-color="${
        HIGHLIGHT_COLOR_MAP[bookmark.color]
      }">
        ${bookmark.text}
      </mark>`;

      this.savedTranscript = htmlDoc.documentElement.outerHTML;

      this.transcriptEditor.commands.setContent(this.savedTranscript);
    },

    async insertBookmark(method = 'timestamp', wordIndex = null) {
      let currentTimeStamp;
      let checkIndex;
      let requestWordIndex;

      if (method === 'timestamp') {
        // Insert bookmark based on timestamp
        if (this.videoComponent) {
          currentTimeStamp = Math.floor(this.videoComponent.currentTime * 1000);
        } else {
          currentTimeStamp = Math.floor(this.audioComponent.currentTime * 1000);
        }

        checkIndex = this.bookmarkList.findIndex(
          (bookmark) => bookmark.timestamp === currentTimeStamp
        );
      } else {
        // Insert bookmark based on word index
        requestWordIndex = wordIndex;
        checkIndex = this.bookmarkList.findIndex(
          (bookmark) => bookmark.wordIndex === requestWordIndex
        );
      }

      if (checkIndex != -1) {
        return;
      }

      const transcriptHTML = this.transcriptEditor.getHTML();
      const captionLines = this.$store.state.user.sessionDetails.transcriptData.sentences;
      const parser = new DOMParser();
      const htmlDoc = parser.parseFromString(transcriptHTML, "text/html");
      const selectedP = htmlDoc.getElementsByTagName("p");

      // Find the paragraph index based on timestamp or word index
      let paragraphIndex;
      if (method === 'timestamp') {
        paragraphIndex = captionLines.findIndex((line) => {
          return line.startTimestamp > currentTimeStamp;
        }) - 1;

        if (paragraphIndex < 0) {
          // check edge cases
          if (currentTimeStamp < captionLines[0].startTimestamp) {
            paragraphIndex = 0;
            // check the beginning
          } else {
            paragraphIndex = captionLines.length - 1;
          }
        }

        // Find word index based on timestamp
        requestWordIndex = captionLines[paragraphIndex].startIndex;
      } else {
        paragraphIndex = captionLines.findIndex((line) => {
          return line.startIndex <= requestWordIndex && line.endIndex >= requestWordIndex;
        });

        // Find timestamp based on word index
        currentTimeStamp = captionLines[paragraphIndex].startTimestamp;
      }

      // API Call to insert bookmark
      let myHeaders = new Headers();
      myHeaders.append(
        "Authorization",
        `Bearer ${JSON.parse(localStorage.getItem("tokenMP")).accessToken}`
      );
      myHeaders.append("Content-Type", "application/json");

      let raw = JSON.stringify({
        timestamp: currentTimeStamp,
        wordIndex: requestWordIndex,
      });

      let requestOptions = {
        method: "POST",
        headers: myHeaders,
        body: raw,
        redirect: "follow",
      };

      const response = await fetch(
        process.env.VUE_APP_MP_SERVER_API_URL +
          `self/uploads/${this.$store.state.user.sessionDetails.mediaUploadData.id}/bookmarks`,
        requestOptions
      );

      const bookmarkData = await response.json();
      if (bookmarkData.status !== "success") {
        console.error(bookmarkData);
        return;
      }

      const rawData = this.$store.state.user.sessionDetails.transcriptData.rawData;
      const currentParagraph = captionLines[paragraphIndex];
      const words = rawData.filter((word) => word.wordIndex <= currentParagraph.endIndex && word.wordIndex >= currentParagraph.startIndex);
      const text = words.map((word) => `<span id="${paragraphIndex}-${word.wordIndex}">${word.word.text}</span>`).join(' ');

      const newBookmark = bookmarkData.data;
      newBookmark.paragraphIndex = paragraphIndex;
      newBookmark.text = text;
      newBookmark.paragraphTimestamp = captionLines[paragraphIndex].startTimestamp;
      const paragraph = [...selectedP].find((element) => {
        return element.id === paragraphIndex.toString();
      });
      paragraph.innerHTML = `<mark
        data-color="${HIGHLIGHT_COLOR_MAP[newBookmark.color]}">
        ${paragraph.innerHTML}
      </mark>`;
      this.savedTranscript = htmlDoc.documentElement.outerHTML;
      this.transcriptEditor.commands.setContent(this.savedTranscript);
      this.bookmarkTracker[paragraphIndex] ||
        (this.bookmarkTracker[paragraphIndex] = []);
      this.bookmarkTracker[paragraphIndex].push(newBookmark);

      this.bookmarkList.push(newBookmark);
      this.bookmarkList.sort(
        (bookmark1, bookmark2) => bookmark1.timestamp - bookmark2.timestamp
      );
      this.$store.commit("user/setSessionDetails", {
        ...this.$store.state.user.sessionDetails,
        bookmarks: this.bookmarkList,
      });
      this.$refs.audioVisualizer?.updateBookmarkList(this.bookmarkList);
      this.$toast.add({
        severity: "contrast",
        summary: `${convertSecondsToMS(currentTimeStamp / 1000)} Bookmarked!`,
        life: 3000,
        group: "bc",
      });
    },

    addToNotes(editor = this.transcriptEditor, value = '') {
      let text;
      if (value && typeof(value) == 'string') {
        text = value;
      } else {
        // Get the selected content as HTML to preserve formatting and line breaks
        const { from, to } = editor.state.selection;
        const selectedContent = editor.state.doc.slice(from, to);
        text = editor.schema.nodeFromJSON(selectedContent.toJSON()).textContent;
      }
      
      // Convert line breaks to HTML paragraphs
      const paragraphs = text.split('\n').filter(line => line.trim() !== '');
      const htmlContent = paragraphs.map(paragraph => `<p>${paragraph}</p>`).join('');
      
      this.pasteToNote(htmlContent);

      if (value) {
        this.closeStudyAssistantModal();
      }
    },

    copyText(editor = this.transcriptEditor, value = '') {
      try {
        let text;
        if (value && typeof(value) == 'string') {
          const parser = new DOMParser();
          const doc = parser.parseFromString(value, 'text/html');
          text = doc.body.textContent || '';
        } else {
          text = editor.state.doc.textBetween(editor.state.selection.from, editor.state.selection.to, '\n');
        }
        navigator.clipboard.writeText(text);
      } catch (error) {
        console.error(error);
      }
    },

    async deleteBookmark() {
      const index =
        this.transcriptEditor.state.selection.$anchor.parent.attrs.id;
      const bookmark = this.bookmarkList.find((bookmark) => {
        return bookmark.paragraphIndex === parseInt(index);
      });
      let myHeaders = new Headers();
      myHeaders.append(
        "Authorization",
        `Bearer ${JSON.parse(localStorage.getItem("tokenMP")).accessToken}`
      );
      myHeaders.append("Content-Type", "application/json");

      let requestOptions = {
        method: "DELETE",
        headers: myHeaders,
        redirect: "follow",
      };

      const response = await fetch(
        process.env.VUE_APP_MP_SERVER_API_URL +
          `self/uploads/${this.$store.state.user.sessionDetails.mediaUploadData.id}/` +
          `bookmarks/${bookmark.id}`,
        requestOptions
      );
      const bookmarkData = await response.json();
      if (bookmarkData.status === "success") {
        this.removeBookmark(bookmark);
      }
    },

    playbackAt(time) {
      this.audioComponent.currentTime = time;
      this.audioComponent.play();
    },

    checkForBookmarkAndRedirect(key, editor) {
      let selectedText = editor.state.doc.textBetween(
          editor.state.selection.from,
          editor.state.selection.to,
          '\n'
      );
      if (selectedText.length !== 0) {
        this.bookmarkPos = null;
      } else {
        selectedText = editor.state.doc.textBetween(
          this.bookmarkPos.from,
          this.bookmarkPos.to,
          '\n'
        );
      }

      switch(key) {
        case 'copy':
          this.copyText(editor, selectedText);
          break;
        case 'notes':
          this.addToNotes(editor, selectedText);
          break;
        default:
          console.error('Not a valid option!');
      }
    },

    checkAndProcessText(key, editor) {
      this.showStudyAssistantModal = true;
      this.studyAssistantModal = {
        header: key,
        body: [],
        isLoading: false,
        errorMessage: '',
        warningMessage: '',
        editor,
      };
      const selectedText = editor.state.doc.textBetween(
          editor.state.selection.from,
          editor.state.selection.to,
          '\n'
      );
      if (selectedText.length !== 0) {
        this.bookmarkPos = null;
      }
      switch(key) {
        case 'Define':
          this.defineText(editor);
          break;
        case 'Simplify':
          this.simplifyText(editor);
          break;
        case 'Rewrite':
          this.rewriteText(editor);
          break;
        case 'Proofread':
          this.proofreadText(editor);
          break;
        default:
          console.error('Not a valid option!');
      }
    },

    simplifyText(editor) {
      if (this.bookmarkPos && this.bookmarkPos?.from >= 0) {
        editor.chain().focus().aiCustomSimplify({
          insert: false,
          position: this.bookmarkPos,
        }).run();
      } else {
        editor.chain().focus().aiSimplify({
          insert: false,
        }).run();
      }
    },

    rewriteText(editor) {
      if (this.bookmarkPos && this.bookmarkPos?.from >= 0) {
        editor.chain().focus().aiCustomRephrase({
          insert: false,
          position: this.bookmarkPos,
        }).run();
      } else {
        editor.chain().focus().aiRephrase({
          insert: false,
        }).run();
      }
    },

    defineText(editor) {
      let position;
      if (this.bookmarkPos && this.bookmarkPos?.from >= 0) {
        position = this.bookmarkPos;
      } else {
        position = editor.state.selection;
      }
      editor.chain().focus().aiCustomDefine({
        insert: false,
        position,
      }).run();
    },

    proofreadText(editor) {
      editor.chain().focus().aiFixSpellingAndGrammar({
        insert: false,
      }).run();
    },

    regenerateAIOutput(editor) {
      editor.chain().aiRegenerate({
        insert: false
      }).run();
    },

    openStudyAssistantModal(status, response) {
      if (status === "loading") {
        const currentBody = [...this.studyAssistantModal.body];
        currentBody.push('Loading');

        this.studyAssistantModal = {
          ...this.studyAssistantModal,
          body: currentBody,
          isLoading: true,
        }

        // Set index to the last element being loaded
        this.studyAssistantModalIndex = currentBody.length - 1;
      } else if (status === "success") {
        const currentBody = [...this.studyAssistantModal.body];

        let text = response.response;
        if (this.studyAssistantModal.header === 'Define') {
          const definitionData = JSON.parse(response.response);
          
          text = `<div class="study-assistant-modal__body__header">${definitionData.word} <p class="study-assistant-modal__body__subtext">|${definitionData.pronunciation}|</p> </div>`;
          text += `<div class="study-assistant-modal__body__subheader">${definitionData.part_of_speech}</div>`;
          text += `<ol class="study-assistant-modal__body__list">`;
          definitionData.definitions.forEach(definition => {
            text += `<li>${definition}</li>`;
          });
          text += `</ol>`;
        }

        if (currentBody[currentBody.length - 1] === "Loading") {
          currentBody[currentBody.length - 1] = text;
        } else {
          currentBody.push(text);
        }

        this.studyAssistantModal = {
          ...this.studyAssistantModal,
          isLoading: false,
          body: currentBody,
          errorMessage: '',
          warningMessage: '',
        }

        if (this.studyAssistantModal.body.length > 1) {
          this.studyAssistantModal.errorMessage = 'Repeated request';
        }
      } else {
        const currentBody = [...this.studyAssistantModal.body];
        if (currentBody[currentBody.length - 1] === "Loading") {
          currentBody.pop();
        }

        if (this.studyAssistantModalIndex === currentBody.length) {
          this.studyAssistantModalIndex = currentBody.length - 1;
        }

        this.studyAssistantModal = {
          ...this.studyAssistantModal,
          isLoading: false,
          errorMessage: 'Oops! Something went wrong.',
          warningMessage: '',
        }
      }
    },

    closeStudyAssistantModal() {
      this.showStudyAssistantModal = false;
      this.studyAssistantModalIndex = 0;
      this.studyAssistantModal = {
        isLoading: false,
        header: '',
        body: [],
        errorMessage: '',
        warningMessage: '',
        editor: null,
      }
    },

    changeStudyAssistantModalIndex(index) {
      if (
        this.studyAssistantModalIndex + index < 0 ||
        this.studyAssistantModalIndex + index >= this.studyAssistantModal.body.length
      ) {
        return;
      }
      this.studyAssistantModalIndex += index;
    },

    updateAudioVideoTime(bookmark) {
      if (
        bookmark >= 0 &&
        this.bookmarkList &&
        this.bookmarkList.length !== 0 &&
        (this.audioComponent || this.videoComponent)
      ) {
        const bookmarkLines = this.$store.state.user.sessionDetails.transcriptData.sentences[bookmark];
        if (this.audioComponent) {
          this.audioComponent.currentTime = bookmarkLines.startTimestamp / 1000;
        }
        if (this.videoComponent) {
          this.videoComponent.currentTime = bookmarkLines.startTimestamp / 1000;
        }
        this.updateBookmarkPos(bookmarkLines);
      }
    },

    updateBookmarkPos(bookmarkLines) {
      const paragraphIndex = bookmarkLines.sentenceIndex;
      
      if (paragraphIndex !== undefined) {
        const paragraphEl = document.getElementById(paragraphIndex.toString());

        if (paragraphEl) {
          const range = document.createRange();
          range.selectNodeContents(paragraphEl);

          const view = this.transcriptEditor.view;
          const from = view.posAtDOM(paragraphEl, 0);
          const to = view.posAtDOM(paragraphEl, paragraphEl.childNodes.length);

          this.bookmarkPos = {
            from,
            to,
          };

          return;
        }
      }

      const fullText = this.transcriptEditor.getText();
      const cleanFullText = fullText.replace(/\s+/g, ' ');
      const cleanBookmarkText = bookmarkLines.sentence.replace(/\s+/g, ' ').trim();
      
      const from = cleanFullText.indexOf(cleanBookmarkText);

      if (from !== -1) {
        const to = from + cleanBookmarkText.length;
        this.bookmarkPos = {
          from,
          to
        };
      }
    },

    async checkTiptapToken() {
      const jsonToken = localStorage.getItem("tokenTiptap");
      if (!jsonToken) {
        await this.getTiptapToken();
        return;
      }
      const token = JSON.parse(jsonToken);
      if (Date.parse(token.accessTokenExpiresAt) < Date.now()) {
        console.log("Access token expired", token.accessTokenExpiresAt);

        await this.getTiptapToken();
      } else {
        this.tiptapToken = token.accessToken;
      }
    },

    async getTiptapToken() {
      let myHeaders = new Headers();
      myHeaders.append(
        "Authorization",
        `Bearer ${JSON.parse(localStorage.getItem("tokenMP")).accessToken}`
      );
      myHeaders.append("Content-Type", "application/json");

      let requestOptions = {
        method: "GET",
        headers: myHeaders,
        redirect: "follow",
      };

      const jsonResponse = await fetch(
        process.env.VUE_APP_MP_SERVER_API_URL + "self/tiptap/token",
        requestOptions
      );

      const response = await jsonResponse.json();

      if (response.status === "error") {
        console.error("Error: ", response);
      } else if (response.status === "success") {
        localStorage.setItem("tokenTiptap", JSON.stringify(response.data));
        this.tiptapToken = response.data.accessToken;
      }
    },

    bookmarkText(editor = this.transcriptEditor) {
      const parentNode = editor.state.selection.$anchor.parent;
      const parentParagraphId = parentNode.attrs.id.split("-")[0];
      const currentSentence = this.$store.state.user.sessionDetails.transcriptData.sentences[parentParagraphId];

      if (currentSentence) {
        this.insertBookmark('word', currentSentence.startIndex);
      }
    },

    startPlaying() {
      // Show toast if AI voice is speaking
      const voiceMap = this.$store.state.user.ttsVoiceBySession;
      if (voiceMap[this.sessionId] !== "default") {
        this.$toast.add({
          severity: "contrast",
          summary: "AI voice is speaking...",
          group: "tts",
        });
      }

      this.$refs.transcriptEditorRef.$el.classList.add("text-editor--scroll");

      this.playing = true;
    },

    pausePlaying() {
      // Remove scrollable paragraph and word
      this.$refs.transcriptEditorRef.transcriptEditor.chain().removeScrollableParagraph().run();
      this.$refs.transcriptEditorRef.transcriptEditor.chain().removeScrollableWord().run();
      this.$refs.transcriptEditorRef.$el.classList.remove("text-editor--scroll");
      this.scrollableParagraphId = null;

      // Remove toast if AI stops speaking
      this.$toast.removeGroup("tts");

      this.playing = false;
    },

    highlightCurrentText() {
      const captionLines = this.$store.state.user.sessionDetails.transcriptData.sentences;
      const rawData = this.$store.state.user.sessionDetails.transcriptData.rawData;
      let currentTimestamp = 0;

      if (this.audioComponent) {
        currentTimestamp = this.audioComponent.currentTime * 1000;
      } else {
        currentTimestamp = this.videoComponent.currentTime * 1000;
      }

      // Find current paragraph index
      const currentParagraphIndex = captionLines.findIndex(line => {
        return line.startTimestamp <= currentTimestamp && line.endTimestamp >= currentTimestamp
      });

      const currentWordIndex = rawData.findIndex(word => {
        return word.word.start * 1000 <= currentTimestamp && word.word.end * 1000 >= currentTimestamp;
      });

      if (this.scrollableParagraphId !== currentParagraphIndex) {
        this.scrollableParagraphId = currentParagraphIndex;
        this.$refs.transcriptEditorRef.transcriptEditor.chain().setCurrentScrollableParagraph(currentParagraphIndex).run();

        if (this.autoScrollEnabled) {
          let scrollParagraph;
          if (this.timestampEnabled || this.speakerEnabled) {
            scrollParagraph = document.getElementById(`${currentParagraphIndex}-header`);
          } else {
            scrollParagraph = document.getElementById(currentParagraphIndex);
          }

          scrollParagraph?.scrollIntoView({ behavior: "smooth" });
        }
      }

      if (currentParagraphIndex >= 0 && currentWordIndex >= 0) {
        const wordId = `${currentParagraphIndex}-${rawData[currentWordIndex].wordIndex}`;
        this.$refs.transcriptEditorRef.transcriptEditor.chain().setCurrentScrollableWord(wordId).run();
      }
    },

    async toggleTimestamp() {
      this.timestampEnabled = !this.timestampEnabled;
      localStorage.setItem("timestampEnabled", this.timestampEnabled.toString());
      this.transcriptChangeInProgress = true;
      await this.setTranscriptText();
      this.$refs.transcriptEditorRef.transcriptEditor.commands.setContent(
        this.savedTranscript
      );
      this.scrollableParagraphId = null;
      this.transcriptChangeInProgress = false;
    },

    async ttsVoiceSelected(voice) {
      if (this.playing) {
        if (this.audioComponent) {
          this.audioComponent.pause();
        } else if (this.videoComponent) {
          this.videoComponent.pause();
        }
        await new Promise((resolve) => setTimeout(resolve, 500));
      }

      
      this.selectedTTSVoice = voice.name;
      this.$store.commit("user/setTTSVoiceBySession", {
        sessionId: this.sessionId,
        voiceId: voice.voiceId
      });

      this.$router.push({
        name: 'SessionInitializer'
      });
    },

    async getVoices() {
      const uploadId = this.$store.state.user.sessionDetails.mediaUploadData.id;

      const headers = new Headers();
      headers.append("content-type", "application/json");
      headers.append(
        "Authorization",
        `Bearer ${JSON.parse(localStorage.getItem("tokenMP")).accessToken}`
      );

      const request = {
        method: "GET",
        headers: headers,
        redirect: "follow",
      };

      const response = await fetch(
        process.env.VUE_APP_MP_SERVER_API_URL.concat(
          "self/uploads/",
          uploadId,
          "/voices/list"
        ),
        request
      );

      const voices = await response.json();
      this.voiceList = voices.data.voices;
      const voiceMap = this.$store.state.user.ttsVoiceBySession;
      this.selectedTTSVoice = this.voiceList.find((voice) => voice.voiceId === voiceMap[this.sessionId]).name;
    },

    addSupplementaryMaterials() {
      // Create a file input element programmatically
      const fileInput = document.createElement('input');
      fileInput.type = 'file';
      fileInput.accept = '.pdf';
      fileInput.style.display = 'none';
      
      fileInput.onchange = async (event) => {
        const target = event.target;
        const file = target.files?.[0];
        if (file) {
          // Create a FileUploadUploaderEvent-like object
          const uploadEvent = { files: file };
          await this.handleSupplementaryUpload(uploadEvent, this.$store.state.user.sessionDetails.id);
        }
        // Clean up
        document.body.removeChild(fileInput);
      };
      
      // Add to DOM and trigger click
      document.body.appendChild(fileInput);
      fileInput.click();
    },

    async handleSupplementaryUpload(event, sessionId) {
      const file = Array.isArray(event.files) ? event.files[0] : event.files;
      const mimeType = file.type;
      const fileName = file.name;
      const uploadType = "mp-supplementary";
    
      this.$toast.add({
        severity: "secondary",
        detail: "Uploading file in progress...",
        group: "notification",
        life: 1000,
      });

      try {
        const uploadedFileInfo = await uploadDocument(fileName, mimeType, file, uploadType, sessionId, this.$store);
        this.$toast.add({
          severity: "secondary",
          summary: "File uploaded successfully",
          detail:
              "Your supplementary file has been uploaded and is now available to the note-taker.",
          life: 3000,
          group: "notification",
        });
      
        // Add uploaded file to store
        await this.$store.dispatch('itemList/addUploadToSession', { sessionId, upload: uploadedFileInfo});
      
        return uploadedFileInfo;
      } catch (err) {
        console.error("File upload failed:", err);
        this.$toast.add({
          severity: "error",
          detail: "File upload failed. Please try again.",
          group: "notification",
          life: 3000,
        });
        throw err;
      }
    },

    readText(editor = this.transcriptEditor) {
      const parentNode = editor.state.selection.$anchor.parent;
      const parentParagraphId = parentNode.attrs.id.split("-")[0];
      const currentSentence = this.$store.state.user.sessionDetails.transcriptData.sentences[parentParagraphId];

      if (currentSentence) {
        editor.commands.setTextSelection(0);
        if (this.videoComponent) {
          this.videoComponent.currentTime = currentSentence.startTimestamp / 1000;
          this.videoComponent.play();
        } else if (this.audioComponent) {
          this.audioComponent.currentTime = currentSentence.startTimestamp / 1000;
          this.audioComponent.play();
        }
      }
    },

    async initializeProcessingState() {
      await this.getVoices();
      this.displayProcessingDialog = true;
      this.loading = false; 
      this.checkVoiceAvailable();
    },

    async checkVoiceAvailable() {
      const MAXIMUM_WAIT_TIME = 600 * 1000; // 10 minutes
      const LOOP_COOLDOWN = 15000; // 15 seconds
      const MAX_ATTEMPTS = MAXIMUM_WAIT_TIME / LOOP_COOLDOWN;
      let attempts = 0;

      const sessionDetails = this.$store.state.user.sessionDetails;
      const voiceMap = this.$store.state.user.ttsVoiceBySession;
      const token = JSON.parse(localStorage.getItem("tokenMP"))?.accessToken;

      if (!token) {
        throw new Error("Authentication token not found.");
      }

      const headers = new Headers({
        "content-type": "application/json",
        "Authorization": `Bearer ${token}`,
      });

      const requestOptions = {
        method: "GET",
        headers: headers,
        redirect: "follow",
      };

      const url = process.env.VUE_APP_MP_SERVER_API_URL.concat(
        `self/uploads/${sessionDetails.mediaUploadData.id}/voices/${voiceMap[sessionDetails.id]}`
      );

      let voiceStatus = 'processing';

      do {
        console.log("Polling for new voice...");
        attempts++;

        if (attempts > MAX_ATTEMPTS) {
          throw new Error("Timed out waiting for voice to be ready.");
        }

        const response = await fetch(url, requestOptions);

        if (!response.ok) {
          throw new Error(`API request failed with status ${response.status}`);
        }

        const result = await response.json();
        voiceStatus = result.status;

        if (voiceStatus === 'processing') {
          await new Promise(resolve => setTimeout(resolve, LOOP_COOLDOWN));
        }

      } while (voiceStatus === 'processing');

      if (voiceStatus === 'success') {
        this.$router.push({ name: 'SessionInitializer' });
      } else if (voiceStatus === 'error') {
        throw new Error("API request failed.");
      }
    },

    selectAnotherVoice() {
      this.openMainMenuDropdown = true;
      this.displayProcessingDialog = false;
    }
  },
};
</script>

<style src="pdfvuer/dist/pdfvuer.css"></style>

<style lang="scss">
$top-bar-height: 8.2rem;
$audio-visualization-bar-height: 40px;
$bottom-bar-height: 8rem;
$border: 0.1rem solid map-get($component-colours, border-light-faint);
$panel-header-height: 3.2rem;

.note-taker-wrapper {
  height: 100%;
  width: 100%;

  .note-taker {
    .left-wrapper {
      position: relative;

      .side-panel {
        background-color: map-get($component-colours, chat-background);
        color: map-get($component-colours, chat-font-primary);
        min-width: $side-panel-width;
        position: absolute;
        top: 0;
        left: 0;
        width: $side-panel-width;
        height: 100%;
        z-index: 2;

        @media only screen and (max-width: 1000px) {
          min-width: $side-panel-width-mobile;
          width: $side-panel-width-mobile;
        }

        ::selection {
          background-color: map-get(
            $component-colours,
            font-background-colour-highlighted
          );
        }
        ::-moz-selection {
          background-color: map-get(
            $component-colours,
            font-background-colour-highlighted
          );
        }
      }
    }

    .content-panel {
      height: 100%;
      width: 100%;
      display: flex;
      flex-direction: column;
      min-width: 0;

      .top-bar {
        display: flex;
        flex-direction: column;
        justify-content: center;
        height: $top-bar-height;
        width: 100%;
        border-bottom: $border;
        padding: 0 2.4rem;
        background-color: map-get($colours, accent-purple-2);

        @media only screen and (max-width: 950px) {
          padding: 0 1.6rem;
        }

        .top-row-wrapper {
          display: flex;
          flex-direction: row;
          justify-content: space-between;
          align-items: center;
        }

        .left-section {
          display: flex;
          align-items: center;
          min-width: 13.2rem;

          @media only screen and (max-width: 1000px) {
            display: none;
          }

          &.side-panel-open {
            @media only screen and (max-width: 1250px) {
              display: none;
            }
          }

          .session-name-wrapper {
            display: flex;
            flex-direction: row;
            align-items: center;

            .session-name {
              @include section-header-2;
              margin-left: 1.6rem;
            }

            .session-name-field {
              display: flex;
              flex-direction: column;
            }

            .session-name-chevron-button {
              img {
                transform: rotate(-90deg);
              }
            }
          }
        }

        .right-section {
          display: flex;
          align-items: center;

          > * {
            margin-left: 1.6rem;
          }

          .tts-voices {
            display: flex;
            gap: 1.6rem;
          }
        }
      }

      .audio-visualization-bar {
        width: 100%;
        height: 0;
        border-bottom: $border;
        border-bottom-width: 0;
        transition: height 0.4s, border-bottom-width 0s 0.4s;
        overflow: auto;
        display: flex;
        align-items: center;
        overflow-y: hidden;

        &.show {
          height: $audio-visualization-bar-height;
          // border-bottom: 0.1rem solid map-get($component-colours, note-taker-border);
          border-bottom-width: 0.1rem;

          transition: height 0.4s, border-bottom-width 0.4s 0s;
        }

        // img {
          // padding: 2rem 2.4rem;
          // height: 100%;
          // width: 100%;
          // object-fit: cover;
          // object-position: left;
        // }

        audio {
          position: absolute;
          bottom: 0;
          z-index: 1000;
          left: 50%;
          transform: translateX(50%);
        }

        .audio-control-bar {
          position: absolute;
          bottom: 2.4rem;
          z-index: 1000;
          left: 50%;
          transform: translateX(-50%);
        }
      }

      .tts-warning {
        background-color: map-get($colours, accent-purple-3);
        font-size: 1.4rem;
        padding: 1rem;
        color: map-get($colours, white);
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 1.4rem;
        &__spinner {
          height: 1.4rem;
          width: 1.4rem;
          margin: unset;
        }
      }

      .main {
        display: flex;
        flex-direction: row;
        flex-grow: 1;
        height: calc(
          100vh - #{$top-bar-height} - #{$audio-visualization-bar-height} - #{$bottom-bar-height}
        );
        position: relative;

        .pdf-overview-panel {
          top: 0;
          left: 50%;
          transform: translateX(-50%);
          margin-top: -0.8rem;
          position: absolute;
        }

        .collapse-button {
          position: absolute;
          background-color: map-get($colours, accent-purple-3);
          padding: 1rem;
          transition: opacity 0.2s;
          height: auto;
          z-index: 2;
        }

        .left-button {
          border-radius: 1.8rem 0 0 1.8rem;
          right: 0;
        }

        .right-button {
          border-radius: 0 1.8rem 1.8rem 0;
          left: 0;
        }

        .note-taker-side-panel {
          border-right: 0.1rem solid
            map-get($component-colours, note-taker-border-dark);
          position: relative;
        }

        .editors {
          background: map-get($colours, accent-purple-2);
          display: flex;
          flex-direction: row;
          flex-grow: 1;
          overflow: auto;
          max-width: 100%;

          .editor-type-header {
            padding: 0 1.6rem;
            // border-bottom: $border;
            align-self: stretch;
            text-align: center;
            height: $panel-header-height;
            min-height: $panel-header-height;
            display: flex;
            align-items: center;
            justify-content: space-between;

            &.tabs {
              padding: 0;
            }

            .editor-type-header-text {
              display: flex;
              flex-direction: row;
              align-items: center;
              font-size: 1.2rem;
              font-weight: bold;
              letter-spacing: 0.015rem;
              color: map-get($colours, grey-1);

              img {
                width: 1.6rem;
                height: 1.6rem;
                margin-top: -0.2rem;
                margin-right: 0.4rem;
              }

              &.clickable {
                background: transparent;
                color: rgba(255, 255, 255, 0.8);
              }
            }

            .note-editor-selection-buttons {
              display: flex;
              flex-direction: row;
              width: 100%;
              height: 100%;

              > * {
                justify-content: center;
                flex: 1;
                border-bottom: 0.1rem solid
                  map-get($colours, translucent-white-10);
                transition: border-color 300ms ease;
              }

              .active-editor-button {
                border-color: map-get($colours, grey-1);
                color: #e8e8e8;
              }
            }
          }

          .download-button {
            position: absolute;
            right: 1.2rem;
            top: 1.2rem;
          }

          .download-button2 {
            margin-right: 1.2rem;
          }

          > *:not(:first-child) {
            border-left: $border;
          }

          .pdf-viewer-panel {
            // align-items: center;

            .pdf-viewer {
              flex-grow: 1;
              padding: 4rem;
              overflow: auto;

              .vue-pdf-embed {
                transform-origin: top left;
              }
            }
          }

          .note-taker-editor-wrapper {
            position: relative;
            height: 100%;
            display: flex;
            flex-direction: column;
            overflow: auto;

            .text-formatting-bar-wrapper {
              // padding: 0.8rem 1.6rem 0;
              padding: 0.4rem 2.8rem 0 1.6rem;
              z-index: 1;
              position: relative;
            }

            .text-editor-wrapper {
              transition: opacity 0.2s 0.2s;
              // height: calc(100% - #{$panel-header-height});
              height: auto;
              flex: 1;
              overflow: auto;

              &.collapsed {
                opacity: 0;
                pointer-events: none;
                transition: opacity 0.2s;
              }
            }

            .timestamp {
              font-size: 1.2rem;
              color: map-get($colours, translucent-white-80);
            }

            .text-editor--scroll {
              .tiptap {
                color: rgba(255, 255, 255, 0.4) !important;
              }

              .timestamp {
                color: rgba(255, 255, 255, 0.4) !important;
              }
            }

            .note-editor {
              // height: calc(100% - #{$bottom-bar-height});

              .text-editor {
                width: auto;
                * :not(a) {
                  color: white !important;
                }
              }
            }

            .disable-personal-note-dialog {
              top: 4.2rem;
            }
          }

          .study-assistant-wrapper {
            width: 75%;
            height: 100%;
            display: flex;
            flex-direction: column;
            transition: all 0.4s;
            transition-property: width, min-width;
            min-width: 30rem;

            &.collapsed {
              width: 0;
              min-width: 0;
              transition: all 0.4s 0.1s;
              transition-property: width, min-width, max-width;
            }
          }
        }
      }
      .bottom-bar {
        bottom: 0;
        background-color: #53435f;
        display: flex;
        flex-direction: row;
        height: $bottom-bar-height;

        .toggle-study-assistant-button {
          margin-top: 1.6rem;
          margin-left: 1.6rem;
          margin-bottom: 2.4rem;
          img {
            border-radius: 8px;
            width: 4rem;
            height: 4rem;
          }
        }
      }
    }

    .dropdown-menu {
      margin-bottom: 1rem;
      z-index: 1000;
    }
  }
}

.note-taker-panel {
  transition: all 0.4s;
  transition-property: width, min-width;
  min-width: 44rem;

  &.collapsed {
    width: 0;
    min-width: 0;
    transition: all 0.4s 0.1s;
    transition-property: width, min-width, max-width;
  }
}

.note-taker-editor-wrapper {
  width: 100%;
}

.note-taker-side-panel {
  min-width: 36rem;
}

.p-overlaypanel.change-session-name-menu {
  border-radius: 1.4rem;
  overflow: hidden;
  transform: translateX(-50%);

  .p-overlaypanel-content {
    padding: 1.6rem 2.4rem;

    .session-name-field {
      display: flex;
      flex-direction: column;
      padding-bottom: 1.6rem;

      input {
        width: 30rem;
      }

      label {
        @include body-2;
        color: map-get($colours, grey-5);
        text-transform: uppercase;
      }
    }

    .button-wrapper {
      padding-top: 0;
      width: 100%;
      display: flex;
      flex-direction: row;
      justify-content: center;

      > *:not(:first-child) {
        margin-left: 1.6rem;
      }
    }
  }
}

.transcript-highlight-menu {
  background-color: map-get($colours, accent-purple-3);
  font-size: 1.6rem;
  display: flex;
  flex-direction: column;
  font-family: $font-secondary;
  border-radius: 0.6rem;

  > * {
    padding: 0.8rem 1.6rem;
  }

  button {
    background-color: transparent;
    text-align: left;
    color: map-get($colours, white);

    &:hover {
      background-color: map-get($colours, accent-purple-4);
    }
  }

  .colour-option {
    border-radius: 50%;
    height: 1.6rem;
    width: 1.6rem;
    margin: 0.8rem;

    &:first-child {
      margin-left: 0;
    }

    &:last-child {
      margin-right: 0;
    }
  }
}

.coming-soon-dialog {
  .p-dialog-content {
    background-color: map-get($colours, accent-purple);
    color: map-get($colours, white);
    width: 50rem;
    border-radius: 1.2rem;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
  }

  .p-dialog-header {
    display: none;
  }
  // align-items: center;

  .coming-soon-dialog-header {
    display: flex;
    justify-content: center;
    font-size: 2.4rem;
    height: 32;
    font-family: $font-accent;
    font-weight: bold;
    padding-bottom: 2.4rem;
  }

  .coming-soon-dialog-iconwrapper {
    display: flex;
    justify-content: flex-end;
    padding: 1.6rem 1.6rem 0;
  }

  .coming-soon-dialog-body {
    display: flex;
    justify-content: center;
    text-align: center;
    font-size: 1.4rem;
  }

  .coming-soon-dialog-buttonwrapper {
    display: flex;
    justify-content: center;

    .coming-soon-dialog-buttonwrapper-button {
      background-color: map-get($colours, accent-purple-2);
      height: 5.6rem;
      width: 16.8rem;
      border-radius: 0.8rem;
      // height: 5rem;
      // width: 10rem;
    }
    margin: 4rem 0;
  }
}

.note-type-toggle-button {
  .p-button-label {
    margin-top: 0.2rem;
  }
}

.p-togglebutton.p-button {
  border-radius: 5rem;
  height: 3rem;
  margin-top: 1rem;
  background: #877594;
  color: #ffffff;
}

.p-togglebutton.p-button:focus.p-highlight {
  background: #877594;
  color: #ffffff;
}

.p-togglebutton.p-button:focus {
  background: #877594;
  color: #ffffff;
}

.p-togglebutton.p-button.p-highlight {
  background: #877594;
  color: #ffffff;
}

.pro-note-alert {
  position: fixed;
  left: 0;
  top: 0;
  height: 100%;
  width: 100%;
  z-index: 9999;
}

.note-taker-video-wrapper {
  width: 100%;
  height: 32vh;
  padding: 1.6rem;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 100;
  background-color: map-get($colours, accent-purple-2);
  // background: blue;
  // transition: height 0.5s, border-bottom-width 0s 0.5s;
  .note-taker-video {
    width: 100%;
    height: 100%;
    border-radius: 1rem;
    cursor: pointer;

    video {
      width: 100%;
      height: 100%;
      border-radius: 1rem;
    }
  }

  .pip-mode-button {
    position: absolute;
    top: 2.5rem;
    right: 4.5rem;
    width: 2.4rem;
    height: 2.4rem;
    opacity: 0;
    transition: opacity 0.3s ease;
    cursor: pointer;
   
    border-radius: 50%;
    color: red;
    font-weight: bold;
    line-height: 2.5rem;
    text-align: center;
  }

  .position-toggle-button {
    right: 10rem;
  }

  /* On hovering the video, show the button */
  // note-taker-video:hover {
  &:hover {
    .pip-mode-button {
      opacity: 1;
      z-index: 100;
    }

    video {
      filter: brightness(50%);
    }
  }
}

.slide-fade-enter-from,
.slide-fade-leave-to {
  transform: translateY(200%);
  opacity: 0;
}

.note-taker-video-collapse {
  display: none;
}

.download-button-wrapper {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  width: 100%;
}

.voice-loading-screen {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 100;
  background-color: map-get($colours, accent-purple-2);
  opacity: 0.8;
  font-size: 1.6rem;

  .spinner {
    stroke: map-get($component-colours, progress-spinner-1);
    height: 10rem;
    width: 10rem;
  }

  .bottom {
    margin-top: 2rem;
  }
}

.processing-dialog {
  .p-dialog-content {
    background-color: map-get($colours, accent-purple-2);
    color: map-get($colours, white);
    width: 45rem;
    border-radius: 1.2rem;
    margin: 0;
    padding: 2rem;
    display: flex;
    flex-direction: column;
  }

  .p-dialog-header {
    display: none;
  }

  &__header {
    text-align: center;
    font-size: 2.4rem;
    height: 32;
    font-family: $font-accent;
    font-weight: bold;
    padding-bottom: 2.4rem;
  }

  &__body {
    text-align: center;
    font-size: 1.4rem;
  }

  &__footer {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    margin-top: 2rem;
    
    .rounded-button {
      color: map-get($colours, black);
      &.pink {
        background-color: map-get($colours, vibrant-purple);
      }
      &.white {
        background-color: map-get($colours, white);
      }
    }
  }
}
</style>
